'use client';

import { Col, Container, Row } from 'react-bootstrap';
import React, { useEffect, useRef } from 'react';

import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';
import { Img } from '@/components/common/ui/images/Img';
import Link from 'next/link';
import { MorphSVGPlugin } from 'gsap/dist/MorphSVGPlugin';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { StyledScrollAnimation } from '@/components/common/ygen/sections/scroll-animation-svg/svg-animations.style';
import { gsap } from 'gsap';
import useDeviceTypeTab from '@/components/hooks/useDeviceTypeTab';
import { useInView } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useShowSetting } from '@/core/context/ShowSettingProvider';

gsap.registerPlugin(ScrollTrigger, MorphSVGPlugin, DrawSVGPlugin);

const ScrollAnimation = ({ data, version, color, place }) => {
	const wrapperRef = useRef(null);
	const isInView = useInView(wrapperRef, { triggerOnce: true });
	const scroll = useShowSetting();
	const logoRef = useRef();
	const bannerImge = data?.images?.list?.find(f => f?.banner === 'on');
	const pathName = usePathname();
	const isMobile = useDeviceTypeTab();

	// Your scroll-related logic
	useEffect(() => {
		if (!scroll?.getScrollData || isMobile) return;

		const wrapper = wrapperRef.current;
		if (!wrapper) return;

		const brands = wrapper.querySelectorAll('.single-item-brand');

		const transforms = [
			'translate(-339%,-267%) scale(1)',
			'translate(-71%,-240%) scale(1)',
			'translate(206%,-315%) scale(1)',
			'translate(393%,-234%) scale(1)',
			'translate(437%,-70%) scale(1)',
			'translate(356%,143%) scale(1)',
			'translate(98%,182%) scale(1)',
			'translate(-197%,176%) scale(1)',
			'translate(-465%,151%) scale(1)',
			'translate(-395%,-71%) scale(1)',
		];

		// Kill only triggers created by this component
		ScrollTrigger.getAll()
			.filter(trigger => trigger.vars?.id?.startsWith('brand-anim-'))
			.forEach(trigger => trigger.kill());

		brands.forEach((brand, index) => {
			const line = brand.querySelector('.line');

			gsap.set(brand, {
				transform: 'translate(-50%, -50%) scale(0.7)',
			});

			gsap.to(brand, {
				transform: transforms[index],
				ease: 'power1.out',
				duration: 0.5,
				delay: index * 0.1,
				scrollTrigger: {
					id: `brand-anim-${pathName}-${index}`, // Make ID unique per route
					trigger: wrapperRef.current,
					start: 'top +=100%',
					end: 'bottom+=200 center',
					scrub: 0.5,
					markers: false,
				},
			});
		});

		return () => {
			ScrollTrigger.getAll()
				.filter(trigger =>
					trigger.vars?.id?.startsWith(`brand-anim-${pathName}`),
				)
				.forEach(trigger => trigger.kill());
		};
	}, [scroll, isInView, isMobile, pathName]);

	const drawTimelines = new Map();

	const createHoverAnimation = (className, options = {}) => {
		// Default options
		const {
			duration = 1,
			stagger = 0.1,
			ease = 'power2.out',
			scale = { from: 0.8, to: 1 },
			opacity = { from: 0, to: 1 },
			repeat = -1,
			yoyo = true,
			transformOrigin = 'center center',
		} = options;

		const onMouseEnter = () => {

			// Target only path elements within the specified class
			const shapes = document.querySelectorAll(
				`${className} path:not(#Path_511-2):not(#Path_522-2):not(#Path_271-2):not(#Path_469-3):not(#Path_469-3):not(#Path_285-2):not(#Path_501-2):not(#Path_481-2):not(#Path_495-2):not(#Path_469-4):not(#Path_475-2):not(#Path_488-2):not(#Path_520-2):not(#Path_469-2)`,
			);

			// Kill any existing timeline for this element
			if (drawTimelines.has(className)) {
				drawTimelines.get(className).kill();
			}

			// Create new timeline
			const timeline = gsap.timeline({ repeat, yoyo });

			// For filled paths, use opacity and scale animations
			timeline.fromTo(
				shapes,
				{
					opacity: opacity.from,
					scale: scale.from,
					transformOrigin,
				},
				{
					duration,
					opacity: opacity.to,
					scale: scale.to,
					stagger,
					ease,
				},
			);

			// Store timeline reference
			drawTimelines.set(className, timeline);
		};

		const onMouseLeave = () => {

			// Target only path elements within the specified class
			const shapes = document.querySelectorAll(`${className} path`);

			// Kill the existing timeline
			if (drawTimelines.has(className)) {
				drawTimelines.get(className).kill();
				drawTimelines.delete(className);
			}

			// Create exit animation with delay
			const exitTimeline = gsap.timeline({
				delay: options.leaveDelay || 0,
			});

			exitTimeline.to(shapes, {
				opacity: opacity.to,
				scale: scale.to,
				duration: options.leaveDuration || 0.3,
				ease: options.leaveEase || 'power2.out',
				stagger: options.leaveStagger || 0,
			});

			// Store exit timeline
			drawTimelines.set(className, exitTimeline);
		};

		return { onMouseEnter, onMouseLeave };
	};

	const { onMouseEnter: siemensEnter, onMouseLeave: siemensLeave } =
		createHoverAnimation('.Siemens_Logo_Link');
	// For other logos
	const { onMouseEnter: phoenixEnter, onMouseLeave: phoenixLeave } =
		createHoverAnimation('.Phoenix_Logo_Link');
	const { onMouseEnter: yokogawaEnter, onMouseLeave: yokogawaLeave } =
		createHoverAnimation('.Yokogawa_Logo_Link');
	const { onMouseEnter: baumerEnter, onMouseLeave: baumerLeave } =
		createHoverAnimation('.Baumer_Logo_Link');
	const { onMouseEnter: jumoEnter, onMouseLeave: jumoLeave } =
		createHoverAnimation('.Jumo_Logo_Link');
	const { onMouseEnter: testo2Enter, onMouseLeave: testo2Leave } =
		createHoverAnimation('.Testo_Logo-2_Link');
	const { onMouseEnter: beamexEnter, onMouseLeave: beamexLeave } =
		createHoverAnimation('.Beamex_Logo_Link');
	const { onMouseEnter: wikaEnter, onMouseLeave: wikaLeave } =
		createHoverAnimation('.Wika_Logo_Link');
	const { onMouseEnter: lesarEnter, onMouseLeave: lesarLeave } =
		createHoverAnimation('.Leser_Logo_Link');
	const { onMouseEnter: brayEnter, onMouseLeave: brayLeave } =
		createHoverAnimation('.Bray_Logo_Link');
	const { onMouseEnter: uwtEnter, onMouseLeave: uwtLeave } =
		createHoverAnimation('.UWTLlogo_Link');
	const { onMouseEnter: cashcoEnter, onMouseLeave: cashcoLeave } =
		createHoverAnimation('.Cashco_Logo_Link');

	return (
		<StyledScrollAnimation
			ref={wrapperRef}
			className={`image-placeholder ${data?.section_data?.subtitle ? data?.section_data?.subtitle : ''} ${version ? 'version' : ''} ${place ? 'place' : ''}`}
			data-scroll-section
			color={color}>
			{!isMobile && <Img src={'/images/static/steptodown.svg'} />}

			<Container fluid>
				<Row>
					<Col md={12}>
						<div
							className={`${version ? 'main-stream' : ''} image-wrapper`}
							data-scroll>
							{!isMobile ? (
								<div className="d-flex animation-wrapper">
									<div className="ygen-logo" ref={logoRef}>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="200"
											height="198"
											viewBox="0 0 200 198">
											<g
												id="Logo_YGEN"
												transform="translate(-530 -875.025)">
												<path
													id="Path_199"
													data-name="Path 199"
													d="M121.011,199.665c41.98,0,76.011-34.623,76.011-77.333S162.99,45,121.011,45,45,79.623,45,122.332,79.031,199.665,121.011,199.665Z"
													transform="translate(509.205 851.693)"
													fill="#fff"
												/>
												<g
													id="Object"
													transform="translate(530 875.025)">
													<path
														id="Path_270"
														data-name="Path 270"
														d="M199.648,107.647a102.375,102.375,0,0,0,.31-11.815,95.494,95.494,0,0,0-1.033-11.419l-19.8-3.3-6.707-18.822,13.187-15.194a100.259,100.259,0,0,0-14.14-18.373L153.52,37.765,137.066,26.533l1.78-20.1A98.638,98.638,0,0,0,117.313.07l-9.334,17.9-19.9.469L77.868,1.011A99.189,99.189,0,0,0,56.048,8.756L58.981,28.73,43.164,40.887,24.733,32.9A99.87,99.87,0,0,0,11.64,52.041l14.024,14.4L20.007,85.612.426,90.047A102.857,102.857,0,0,0,.049,102.18a109.653,109.653,0,0,0,1,11.078l19.8,3.35L27.5,135.45,14.206,150.611a101.623,101.623,0,0,0,14.134,18.47l18-8.994,16.42,11.289L60.9,191.494a99.967,99.967,0,0,0,22.194,6.576l9.25-17.941,19.9-.536L122.5,196.971a100.254,100.254,0,0,0,21.8-7.835l-3-19.964,15.764-12.227,18.447,7.946A100.1,100.1,0,0,0,188.564,145.7L174.457,131.35l5.58-19.221Zm-56.335,40.894a65.376,65.376,0,0,1-92.563-6.03,66.158,66.158,0,0,1,5.924-93.053,65.456,65.456,0,0,1,92.616,6.056,66.147,66.147,0,0,1-5.987,93.026Z"
														transform="translate(-0.009 -0.07)"
														fill="#0b304e"
													/>
												</g>
												<g
													id="Group_35"
													data-name="Group 35"
													transform="translate(611.265 1024.684)">
													<path
														id="Path_202"
														data-name="Path 202"
														d="M94.721,150.088h3.39l1.966,3.288,1.967-3.288h3.39l-3.781,5.662v4.085H98.636V155.75Z"
														transform="translate(-94.721 -149.98)"
														fill="#0b304e"
													/>
													<path
														id="Path_203"
														data-name="Path 203"
														d="M107.795,156.323V154.29h4.695v4.153a9.389,9.389,0,0,1-2.356,1.237,8.064,8.064,0,0,1-2.44.339,5.991,5.991,0,0,1-2.831-.594,4.154,4.154,0,0,1-1.7-1.694,5.76,5.76,0,0,1-.594-2.695,5.557,5.557,0,0,1,.662-2.763,4.181,4.181,0,0,1,1.916-1.8,6.214,6.214,0,0,1,2.678-.475,7.546,7.546,0,0,1,2.407.288,3.249,3.249,0,0,1,1.339.915,4.1,4.1,0,0,1,.78,1.559l-2.9.508a1.545,1.545,0,0,0-.61-.83,1.781,1.781,0,0,0-1.086-.305,1.967,1.967,0,0,0-1.576.7,3.222,3.222,0,0,0-.594,2.169,3.477,3.477,0,0,0,.594,2.272,2.121,2.121,0,0,0,1.694.678,3.03,3.03,0,0,0,.967-.152,5.551,5.551,0,0,0,1.05-.492v-.915Z"
														transform="translate(-93.523 -149.994)"
														fill="#0b304e"
													/>
													<path
														id="Path_204"
														data-name="Path 204"
														d="M111.517,150.088H119.6v2.085h-5.084v1.542H119.2V155.7h-4.678v1.916H119.7v2.22h-8.2Z"
														transform="translate(-92.16 -149.98)"
														fill="#0b304e"
													/>
													<path
														id="Path_205"
														data-name="Path 205"
														d="M118.9,150.088h2.8l3.679,5.391v-5.391h2.83v9.747h-2.83l-3.645-5.356v5.356H118.9Z"
														transform="translate(-91.031 -149.98)"
														fill="#0b304e"
													/>
												</g>
												<g
													id="Group_34"
													data-name="Group 34"
													transform="translate(582.444 927.039)">
													<path
														id="Path_200"
														data-name="Path 200"
														d="M116.868,64.318A45.765,45.765,0,1,1,99.2,67.826,46.123,46.123,0,0,1,116.868,64.318Zm0-.848a46.987,46.987,0,1,0,33.224,13.762A46.987,46.987,0,0,0,116.868,63.471Z"
														transform="translate(-69.882 -63.471)"
														fill="#ec2527"
													/>
													<path
														id="Path_201"
														data-name="Path 201"
														d="M115.844,71.159a38,38,0,1,1-14.685,2.879A38.258,38.258,0,0,1,115.844,71.159Zm0-.865A39.119,39.119,0,1,0,143.5,81.759,39.121,39.121,0,0,0,115.844,70.294Z"
														transform="translate(-68.841 -62.429)"
														fill="#ec2527"
													/>
													<path
														id="Path_206"
														data-name="Path 206"
														d="M114.172,105.9l-7.763,12.068L83.441,85.221h17.086Z"
														transform="translate(-67.813 -60.151)"
														fill="#0b304e"
													/>
													<path
														id="Path_207"
														data-name="Path 207"
														d="M126.441,82.484h19.951l-9.984-15.425Z"
														transform="translate(-61.251 -62.923)"
														fill="#ae1f26"
													/>
													<path
														id="Path_208"
														data-name="Path 208"
														d="M128.763,81.853l-2.492,3.882-22.036,34.188v34.138a48.239,48.239,0,0,0,7.391.576,46.421,46.421,0,0,0,7.272-.576V120.008l24.086-34.273,2.729-3.882ZM114.778,151.79h-6.289v-1.306h6.289Zm0-2.611h-6.289v-1.322h6.289Z"
														transform="translate(-64.64 -60.665)"
														fill="#0b304e"
													/>
												</g>
											</g>
										</svg>
									</div>

									<div
										className="siemens single-item-brand"
										id={'siemens'}>
										<Link
											href={'ready-stock?brand=siemens'}
											target={'_blank'}
											onMouseEnter={siemensEnter}
											onMouseLeave={siemensLeave}
											className={'Siemens_Logo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="108"
												height="108"
												viewBox="0 0 108 108">
												<g
													id="Siemens_Logo"
													transform="translate(-435 -113.5)">
													<path
														id="Path_522"
														data-name="Path 522"
														d="M185,113a54,54,0,1,0-54-54A54,54,0,0,0,185,113Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<path
														id="Path_523"
														data-name="Path 523"
														d="M155.205,53.37v2.209A10,10,0,0,0,152.077,55c-1.136,0-1.774.345-1.774.957,0,.4.28.664,1.072,1.021l2.132.958a3.624,3.624,0,0,1,2.553,3.332c0,2.247-1.94,3.664-5.017,3.664a17.877,17.877,0,0,1-3.83-.472v-2.3a14.372,14.372,0,0,0,3.575.575c1.276,0,1.953-.357,1.953-1.008,0-.409-.255-.689-.868-.97l-2.336-1.034a3.525,3.525,0,0,1-2.554-3.294c0-2.119,1.877-3.472,4.8-3.472a15.079,15.079,0,0,1,3.345.409Z"
														transform="translate(304 108.5)"
														fill="#00a9b0"
														fillRule="evenodd"
													/>
													<path
														id="Path_524"
														data-name="Path 524"
														d="M157.732,53.243h3.447v11.5h-3.447Z"
														transform="translate(304 108.5)"
														fill="#00a9b0"
														fillRule="evenodd"
													/>
													<path
														id="Path_525"
														data-name="Path 525"
														d="M172.4,53.243v2.106h-5.208v2.592h4.545v1.915h-4.545v2.694h5.272v2.2h-8.63v-11.5Z"
														transform="translate(304 108.5)"
														fill="#00a9b0"
														fillRule="evenodd"
													/>
													<path
														id="Path_526"
														data-name="Path 526"
														d="M188.69,53.243v11.5h-3.345v-7.57l-3.268,7.672h-2.106l-3.243-7.672v7.57h-2.311v-11.5h4.213l2.975,7,2.961-7Z"
														transform="translate(304 108.5)"
														fill="#00a9b0"
														fillRule="evenodd"
													/>
													<path
														id="Path_527"
														data-name="Path 527"
														d="M199.911,53.243v2.106H194.69v2.592h4.545v1.915H194.69v2.694h5.272v2.2h-8.629v-11.5Z"
														transform="translate(304 108.5)"
														fill="#00a9b0"
														fillRule="evenodd"
													/>
													<path
														id="Path_528"
														data-name="Path 528"
														d="M212.051,53.243v11.5h-3.676l-4.149-7.379v7.379h-2.3v-11.5h3.778l4.047,7.277V53.243Z"
														transform="translate(304 108.5)"
														fill="#00a9b0"
														fillRule="evenodd"
													/>
													<path
														id="Path_529"
														data-name="Path 529"
														d="M222.06,53.37v2.209a10.287,10.287,0,0,0-3.1-.574c-1.162,0-1.787.332-1.787.957,0,.409.255.651,1.072,1.021l2.132.958a3.638,3.638,0,0,1,2.553,3.345c0,2.234-1.953,3.651-5.017,3.651a17.966,17.966,0,0,1-3.83-.472v-2.3a14.456,14.456,0,0,0,3.575.575c1.276,0,1.966-.357,1.966-1,0-.421-.243-.689-.881-.983l-2.324-1.034c-1.748-.779-2.553-1.813-2.553-3.294,0-2.119,1.877-3.472,4.8-3.472a14.979,14.979,0,0,1,3.345.409Z"
														transform="translate(304 108.5)"
														fill="#00a9b0"
														fillRule="evenodd"
													/>
												</g>
											</svg>
										</Link>
									</div>
									<div className="phoenix single-item-brand">
										<Link
											href={'/ready-stock?brand=yokogawa'}
											target={'_blank'}
											onMouseEnter={yokogawaEnter}
											onMouseLeave={yokogawaLeave}
											className={'Yokogawa_Logo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="127.025"
												height="127.025"
												viewBox="0 0 127.025 127.025">
												<g
													id="Yokogawa_Logo"
													transform="translate(-773 -113.5)">
													<path
														id="Path_501"
														data-name="Path 501"
														d="M532.512,132.025A63.512,63.512,0,1,0,469,68.512,63.512,63.512,0,0,0,532.512,132.025Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<path
														id="Path_502"
														data-name="Path 502"
														d="M547.851,61.04a.183.183,0,0,0,.121-.021.179.179,0,0,0,.08-.093,3.4,3.4,0,0,0,.259-.762c.246-1.14.579-2.255.875-3.376.177-.68.359-1.361.536-2.041.108-.41.195-.819.3-1.228a3.806,3.806,0,0,1,.132-.378.474.474,0,0,1,.41-.315,4.356,4.356,0,0,1,.906-.031,4.694,4.694,0,0,1,.511.057.286.286,0,0,1,.132.047.279.279,0,0,1,.094.1,1.922,1.922,0,0,1,.171.372c.108.372.2.743.3,1.121l.485,1.921c.277,1.083.566,2.167.843,3.25.063.258.108.523.171.775a2.975,2.975,0,0,0,.132.378.648.648,0,0,0,.094.164c.07.094.114.094.19,0a.884.884,0,0,0,.144-.252,2.756,2.756,0,0,0,.151-.5c.227-1.335.63-2.633.921-3.95.169-.724.352-1.455.529-2.186.044-.189.075-.384.114-.573.088-.454.264-.63.731-.661a4.846,4.846,0,0,1,1,0c.3.038.34.076.327.378a3.681,3.681,0,0,1-.177.863c-.157.542-.333,1.083-.484,1.631-.693,2.425-1.374,4.863-2.06,7.294-.082.3-.171.586-.246.882a.222.222,0,0,1-.183.189,3.5,3.5,0,0,1-.5.094,4.73,4.73,0,0,1-.99,0,.472.472,0,0,1-.391-.265,3.21,3.21,0,0,1-.264-.762c-.428-1.89-.964-3.691-1.437-5.537-.081-.321-.144-.63-.22-.976a2.223,2.223,0,0,0-.24-.63c-.075-.12-.175-.113-.24,0a2.715,2.715,0,0,0-.277.781c-.334,1.468-.768,2.91-1.159,4.359-.175.668-.334,1.342-.5,2.009-.051.227-.132.441-.2.661a.439.439,0,0,1-.39.315,5.491,5.491,0,0,1-.907.057,2.092,2.092,0,0,1-.485-.038.426.426,0,0,1-.22-.077.421.421,0,0,1-.145-.181,3.572,3.572,0,0,1-.259-.75c-.132-.523-.252-1.052-.389-1.575q-.813-3.1-1.644-6.192c-.127-.466-.278-.926-.41-1.4-.069-.239-.106-.491-.157-.737a.471.471,0,0,1,0-.1.248.248,0,0,1,0-.1.234.234,0,0,1,.209-.185.7.7,0,0,1,.226-.038l.819-.031a2.478,2.478,0,0,1,.315,0,.409.409,0,0,1,.385.29c.057.183.12.359.163.535.082.353.145.712.227,1.064.195.857.4,1.707.63,2.564.169.705.34,1.417.5,2.123.069.3.108.63.177.907a4.251,4.251,0,0,0,.151.485A.705.705,0,0,0,547.851,61.04Z"
														transform="translate(319.717 118.523)"
													/>
													<path
														id="Path_503"
														data-name="Path 503"
														d="M537.6,61.227V62.8a7.558,7.558,0,0,1-.044,1c-.045.328-.082.328-.347.384a1.657,1.657,0,0,1-.629,0,.384.384,0,0,1-.3-.239c-.044-.107-.069-.227-.108-.34a2.246,2.246,0,0,0-.126-.359c-.063-.139-.113-.151-.252-.082a2.683,2.683,0,0,0-.365.214c-.271.183-.542.372-.806.567a2.665,2.665,0,0,1-1.3.485,5.671,5.671,0,0,1-3.547-.731,4.273,4.273,0,0,1-1.467-1.417,7.143,7.143,0,0,1-1.121-4.1,6.193,6.193,0,0,1,1.121-3.427,5,5,0,0,1,2.8-1.953,4.857,4.857,0,0,1,.63-.1,5.529,5.529,0,0,1,1.02-.088,6.487,6.487,0,0,1,2.186.435,3.961,3.961,0,0,1,2.192,2.148c.075.183.157.365.226.554a1.4,1.4,0,0,1,.07.359.244.244,0,0,1-.04.191.236.236,0,0,1-.074.067.242.242,0,0,1-.094.031,2.173,2.173,0,0,1-.34.069,3.294,3.294,0,0,1-1,0h-.1a.258.258,0,0,1-.131-.061.251.251,0,0,1-.077-.122c-.037-.113-.094-.227-.139-.34a2.517,2.517,0,0,0-1.373-1.468,3.2,3.2,0,0,0-2.715,0,3.366,3.366,0,0,0-1.89,2.016,5.821,5.821,0,0,0-.34,2.52,5.229,5.229,0,0,0,.523,1.89,3.707,3.707,0,0,0,.925,1.26,3,3,0,0,0,1.89.743,3.932,3.932,0,0,0,1.5-.239,2.516,2.516,0,0,0,1.605-1.682c.082-.239.12-.491.177-.731a.577.577,0,0,0,0-.183.28.28,0,0,0-.011-.118.291.291,0,0,0-.054-.105.307.307,0,0,0-.092-.076.3.3,0,0,0-.114-.035,1.833,1.833,0,0,0-.415-.05h-.681c-.226,0-.629.038-.932.038a1.626,1.626,0,0,1-.391-.057.366.366,0,0,1-.3-.378,2.348,2.348,0,0,1,0-.724V58.45a.234.234,0,0,1,.24-.189c.183,0,.365-.038.548-.038h2.941a4.74,4.74,0,0,1,.731.038.256.256,0,0,1,.156.056.253.253,0,0,1,.09.139,2.19,2.19,0,0,1,.082.422,4.3,4.3,0,0,1,0,.586,12.183,12.183,0,0,0,0,1.26v.5Z"
														transform="translate(316.204 118.487)"
													/>
													<path
														id="Path_504"
														data-name="Path 504"
														d="M511.5,58.461a6.468,6.468,0,0,1-1.014,3.578,5.281,5.281,0,0,1-3.433,2.331,5.661,5.661,0,0,1-1.568.113,4.834,4.834,0,0,1-3.093-1.4,5.816,5.816,0,0,1-1.556-2.734,6.84,6.84,0,0,1,.511-4.97,4.872,4.872,0,0,1,2.98-2.52,3.872,3.872,0,0,1,.6-.12l.725-.12.108-.031a5.8,5.8,0,0,1,2.677.579,5.206,5.206,0,0,1,2.43,2.52,6.363,6.363,0,0,1,.6,2.406A1.9,1.9,0,0,1,511.5,58.461Zm-1.965.239a5.686,5.686,0,0,0-.208-1.7,4.213,4.213,0,0,0-.951-1.72,3.334,3.334,0,0,0-2.52-1.058.358.358,0,0,0-.114,0,6.344,6.344,0,0,0-1.083.34,2.9,2.9,0,0,0-1.7,1.726,6.4,6.4,0,0,0-.353,1.82,5.9,5.9,0,0,0,.322,2.406,4.107,4.107,0,0,0,.939,1.568,2.713,2.713,0,0,0,2.072.813,5.431,5.431,0,0,0,.774-.1,3.02,3.02,0,0,0,1.261-.523,3.582,3.582,0,0,0,1.02-1.26A5.3,5.3,0,0,0,509.531,58.7Z"
														transform="translate(310.624 118.482)"
													/>
													<path
														id="Path_505"
														data-name="Path 505"
														d="M517.481,58.64a6.936,6.936,0,0,1,.882-3.477,4.958,4.958,0,0,1,2.306-2.1,4.406,4.406,0,0,1,1.361-.346l.454-.076a2.361,2.361,0,0,1,.365,0,5.723,5.723,0,0,1,2.519.573,5.188,5.188,0,0,1,2.432,2.52,6.21,6.21,0,0,1,.579,2.23,6.484,6.484,0,0,1-.668,3.521,5.294,5.294,0,0,1-3.886,2.916,5.205,5.205,0,0,1-2.52-.12,4.97,4.97,0,0,1-2.52-1.808,6.183,6.183,0,0,1-1.153-2.658c-.031-.176-.056-.353-.075-.535S517.488,58.791,517.481,58.64Zm8.97.17a5.674,5.674,0,0,0-.069-1.128,4.922,4.922,0,0,0-.687-1.89,3.376,3.376,0,0,0-2.9-1.625.682.682,0,0,0-.163,0,7.945,7.945,0,0,0-1,.309,2.952,2.952,0,0,0-1.789,1.883,6.317,6.317,0,0,0-.391,2.28,5.99,5.99,0,0,0,.391,2.022,3.916,3.916,0,0,0,1.008,1.512,2.716,2.716,0,0,0,1.934.712,6.786,6.786,0,0,0,.8-.095,2.955,2.955,0,0,0,1.707-.894,3.77,3.77,0,0,0,.529-.718A5.136,5.136,0,0,0,526.451,58.81Z"
														transform="translate(314.169 118.491)"
													/>
													<path
														id="Path_506"
														data-name="Path 506"
														d="M510.445,58.689V55.81c0-.5,0-1-.044-1.493a3.764,3.764,0,0,1,.069-1,1.184,1.184,0,0,1,.126-.3.373.373,0,0,1,.34-.189h.87a2.015,2.015,0,0,1,.334.044.246.246,0,0,1,.22.227c0,.139.044.277.057.416.037.63,0,1.222,0,1.833v1.713a2.581,2.581,0,0,0,.051.491c.038.2.113.22.277.107a2.406,2.406,0,0,0,.334-.283l.976-1.052,3.194-3.257a.506.506,0,0,1,.283-.157,5.413,5.413,0,0,1,1.461-.113,3.388,3.388,0,0,1,.44.063c.177.044.2.1.108.252a2.284,2.284,0,0,1-.277.365c-.4.416-.807.825-1.216,1.228-.63.63-1.261,1.2-1.89,1.8a6.387,6.387,0,0,0-.435.491,2.364,2.364,0,0,0-.195.283.209.209,0,0,0,0,.265l1.663,2.368q.989,1.367,1.933,2.715c.132.189.283.359.422.535a5.01,5.01,0,0,1,.308.422,1.613,1.613,0,0,1,.132.346c0,.1,0,.126-.087.157a1.906,1.906,0,0,1-.3.076,5.152,5.152,0,0,1-1.556,0H517.9a.407.407,0,0,1-.227-.158c-.882-1.26-1.776-2.52-2.664-3.817-.082-.12-.151-.246-.233-.365s-.214-.327-.334-.491a2.378,2.378,0,0,0-.208-.239.308.308,0,0,0-.11-.09.314.314,0,0,0-.138-.032.314.314,0,0,0-.249.122,2.294,2.294,0,0,0-.3.3,4.8,4.8,0,0,1-.478.479,5.347,5.347,0,0,0-.379.378.543.543,0,0,0-.126.353v3.288a.29.29,0,0,1-.258.29,3.637,3.637,0,0,1-.379.063,3.586,3.586,0,0,1-.97-.031c-.246-.05-.315-.12-.34-.372a3.144,3.144,0,0,1,0-.769C510.458,61.536,510.445,60.068,510.445,58.689Z"
														transform="translate(312.683 118.524)"
													/>
													<path
														id="Path_507"
														data-name="Path 507"
														d="M541.427,52.793l.756.031a.521.521,0,0,1,.454.309c.114.246.232.485.321.737.725,2.135,1.587,4.22,2.394,6.3.3.787.63,1.556.945,2.337a7.955,7.955,0,0,1,.377,1.134,1.308,1.308,0,0,1,.045.265c0,.12,0,.145-.126.176a2.383,2.383,0,0,1-.353.05h-1.2a.428.428,0,0,1-.178-.056.436.436,0,0,1-.138-.126,2.371,2.371,0,0,1-.208-.391c-.113-.34-.2-.687-.309-1.033-.069-.246-.138-.5-.232-.743a2.272,2.272,0,0,0-.2-.391.54.54,0,0,0-.46-.258c-.454,0-.907-.038-1.361,0-.629,0-1.259,0-1.89.063a6.6,6.6,0,0,0-.788.094.248.248,0,0,0-.214.189c-.3.813-.629,1.625-.919,2.438a.3.3,0,0,1-.083.139.3.3,0,0,1-.143.076c-.151,0-.3.057-.454.069a3.379,3.379,0,0,1-.945-.044,1.971,1.971,0,0,1-.208-.057.136.136,0,0,1-.081-.056.133.133,0,0,1-.019-.1,3.494,3.494,0,0,1,.063-.372,7.027,7.027,0,0,1,.415-1.115c.416-.913.756-1.89,1.115-2.8.579-1.487,1.147-2.973,1.713-4.46.139-.365.246-.737.379-1.1a5.888,5.888,0,0,1,.315-.7,1.754,1.754,0,0,1,.277-.378.512.512,0,0,1,.179-.13.506.506,0,0,1,.218-.04h.536Zm0,2.274c-.038.063-.069.1-.088.139l-.138.3q-.63,1.562-1.153,3.149c-.076.239-.145.485-.2.724s0,.17.126.2a4.066,4.066,0,0,0,.555.076,13.99,13.99,0,0,0,1.89,0,3.946,3.946,0,0,0,.466-.063c.107,0,.114,0,.107-.132a.489.489,0,0,0,0-.145c-.031-.145-.063-.3-.107-.447a23.183,23.183,0,0,0-.87-2.538c-.144-.346-.3-.687-.454-1.027a1.946,1.946,0,0,0-.126-.233Z"
														transform="translate(318.096 118.525)"
													/>
													<path
														id="Path_508"
														data-name="Path 508"
														d="M559.7,52.793l.768.038a.545.545,0,0,1,.272.088.557.557,0,0,1,.189.214,5.406,5.406,0,0,1,.346.806c.592,1.833,1.354,3.6,2.035,5.392.415,1.1.857,2.2,1.259,3.3.139.372.24.75.359,1.121a1,1,0,0,1,.031.189c0,.12,0,.158-.132.189a.548.548,0,0,1-.177,0,5.037,5.037,0,0,1-1.373-.076h-.063a.315.315,0,0,1-.259-.227c-.3-.813-.63-1.625-.925-2.438a.214.214,0,0,0-.074-.113.209.209,0,0,0-.128-.044c-.4-.038-.788-.088-1.184-.1h-2.866a.487.487,0,0,0-.241.067.493.493,0,0,0-.175.178,2.817,2.817,0,0,0-.232.479c-.12.384-.22.781-.334,1.165-.069.233-.151.454-.234.68a.629.629,0,0,1-.705.447h-1.033a1.467,1.467,0,0,1-.226-.05.12.12,0,0,1-.045-.016.131.131,0,0,1-.035-.032.114.114,0,0,1-.019-.043.128.128,0,0,1,0-.047,1.775,1.775,0,0,1,.031-.252,7.224,7.224,0,0,1,.41-1.209c1.02-2.488,1.953-5.039,2.929-7.515.114-.283.189-.586.283-.876a3.917,3.917,0,0,1,.309-.743,1.12,1.12,0,0,1,.3-.378.632.632,0,0,1,.4-.151h.524Zm0,2.274c-.044.076-.081.126-.107.183-.139.315-.277.63-.4.951-.365.958-.711,1.89-.994,2.9a3.213,3.213,0,0,0-.082.353c0,.076,0,.094.076.113a1.452,1.452,0,0,0,.238.044,16.835,16.835,0,0,0,1.954.094,5.974,5.974,0,0,0,.674-.038c.226,0,.308-.158.264-.378a2.955,2.955,0,0,0-.081-.321c-.233-.775-.517-1.537-.819-2.293-.189-.466-.4-.926-.593-1.386a.777.777,0,0,0-.106-.221Z"
														transform="translate(321.934 118.525)"
													/>
													<path
														id="Path_509"
														data-name="Path 509"
														d="M493.986,52.793l.762.031A.553.553,0,0,1,495,52.9a.547.547,0,0,1,.189.183,8.061,8.061,0,0,1,.63,1c.466.806.919,1.625,1.386,2.438.132.233.264.466.409.693a3.008,3.008,0,0,0,.252.321,1.4,1.4,0,0,0,.151.113.108.108,0,0,0,.076.031.106.106,0,0,0,.075-.031,1.77,1.77,0,0,0,.189-.189,2.042,2.042,0,0,0,.177-.283c.252-.517.554-1.008.844-1.505s.705-1.159,1.027-1.739c.1-.176.189-.365.283-.542a2.4,2.4,0,0,1,.233-.353.413.413,0,0,1,.127-.112.432.432,0,0,1,.162-.052,5.674,5.674,0,0,1,1.033-.044,4.293,4.293,0,0,1,.573.05c.22,0,.24.075.163.277a1.889,1.889,0,0,1-.145.315l-.264.454q-1.21,1.965-2.426,3.93c-.195.315-.409.63-.63.926-.119.189-.246.372-.352.561a1.575,1.575,0,0,0-.177.762v1.26a17.915,17.915,0,0,1,0,1.839c-.016.186-.041.371-.075.554a.888.888,0,0,1-.082.227.264.264,0,0,1-.22.158,4.055,4.055,0,0,1-1.2,0l-.151-.057a.227.227,0,0,1-.127-.092.222.222,0,0,1-.036-.153c0-.706,0-1.411-.031-2.123s0-1.26,0-1.89a.373.373,0,0,0-.063-.208q-1.45-2.381-2.9-4.762c-.133-.214-.284-.416-.422-.63s-.322-.5-.467-.75a2.151,2.151,0,0,1-.157-.391c-.038-.126,0-.17.114-.221a.868.868,0,0,1,.22-.05h.63Z"
														transform="translate(309.042 118.525)"
													/>
													<path
														id="Path_510"
														data-name="Path 510"
														d="M492.227,58.242l-6.179-6.173-6.185,6.192a15.16,15.16,0,0,1,6.179,6.406A15.3,15.3,0,0,1,492.227,58.242Z"
														transform="translate(306.278 118.373)"
														fill="#ff0"
													/>
												</g>
											</svg>
										</Link>
									</div>
									<div className="pneucon single-item-brand">
										<Link
											href={'/ready-stock?brand=beamex'}
											target={'_blank'}
											onMouseEnter={beamexEnter}
											onMouseLeave={beamexLeave}
											className={'Beamex_Logo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="116.775"
												height="116.775"
												viewBox="0 0 116.775 116.775">
												<g
													id="Beamex_Logo"
													transform="translate(-887 -464.5)">
													<path
														id="Path_481"
														data-name="Path 481"
														d="M641.388,472.775A58.388,58.388,0,1,0,583,414.388,58.387,58.387,0,0,0,641.388,472.775Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<path
														id="Path_482"
														data-name="Path 482"
														d="M647.247,407.112a8.281,8.281,0,0,0-.247,1.319v8.585c0,.847-.247,1.177-1.177,1.177-1.179-.059-2.356,0-3.534,0-.671,0-.883-.247-.872-.895v-8.586a8.975,8.975,0,0,0-.294-1.555h-.4a7.379,7.379,0,0,0-.259,1.3v8.6c0,.836-.224,1.179-1.107,1.179-1.307-.059-2.626,0-3.934,0-.554,0-.813-.165-.813-.766V407.347c0-1.177.836-1.908,2.2-1.92h14.757a1.943,1.943,0,0,1,2.1,2.073v9.833c0,.649-.223.919-.883.908H648.66c-.683,0-.847-.283-.847-.92v-8.5a11.2,11.2,0,0,0-.283-1.625Z"
														transform="translate(308.09 112.417)"
														fill="#009946"
													/>
													<path
														id="Path_483"
														data-name="Path 483"
														d="M601.9,405.577h4.593c1.636,0,2.355.731,2.355,2.356v8.138a2.037,2.037,0,0,1-2.285,2.355h-9.422c-.765,0-1.048-.212-1.048-1.012V404.082c0-.718.2-1.024.966-1h4.028c.671,0,.907.235.86.883S601.9,404.882,601.9,405.577Zm.506,1.872c-.765.848-.741,8.244,0,9.056.707-.835.683-8.455,0-9.114Z"
														transform="translate(305.038 112.231)"
														fill="#009946"
													/>
													<path
														id="Path_484"
														data-name="Path 484"
														d="M628.031,407.165a6.394,6.394,0,0,0-.271,1.106c0,.577-.295.778-.847.766h-4.3c-.636,0-.813-.259-.825-.86-.059-2.108.612-2.838,2.7-2.838h7.678c1.7,0,2.414.707,2.414,2.356v8.138a2,2,0,0,1-2.237,2.285h-8.326A2.025,2.025,0,0,1,621.8,416V410.8c0-.625.247-.836.859-.825h5.194c.624,0,.895-.213.835-.825l-.212-1.99Zm.213,4.711c-.707.741-.7,3.721,0,4.381a5.21,5.21,0,0,0,0-4.37Z"
														transform="translate(307.074 112.41)"
														fill="#009946"
													/>
													<path
														id="Path_485"
														data-name="Path 485"
														d="M659.964,416.3a4.735,4.735,0,0,0,.307-1.107c0-.53.176-.753.73-.753h4.464c.494,0,.718.177.7.671s0,.659,0,.99a1.894,1.894,0,0,1-2.026,2.013h-8.667c-1.378,0-2.1-.871-2.109-2.449v-8.138a2.016,2.016,0,0,1,2.215-2.133H663.9a1.956,1.956,0,0,1,2.2,2.191v5.1c0,.671-.223.883-.883.872h-4.57c-1.531,0-1.414-.141-1.39,1.46a7.55,7.55,0,0,0,.224,1.343Zm-.141-9.151a4.716,4.716,0,0,0,0,4.394,4.926,4.926,0,0,0,0-4.394Z"
														transform="translate(309.576 112.413)"
														fill="#009946"
													/>
													<path
														id="Path_486"
														data-name="Path 486"
														d="M615.515,416.434a3.558,3.558,0,0,0,.318-1.048c0-.848.389-.966,1.106-.93h3.934c.636,0,.907.165.942.836.106,2.026-.659,2.861-2.7,2.861h-7.785c-1.6,0-2.355-.694-2.355-2.273v-8.315a1.955,1.955,0,0,1,2.2-2.214h8.326a2,2,0,0,1,2.249,2.191v5.1c0,.659-.271.883-.918.871h-5.193c-.624,0-.872.212-.813.836S614.667,415.739,615.515,416.434Zm-.165-9.279a4.915,4.915,0,0,0,0,4.393,4.71,4.71,0,0,0-.046-4.393Z"
														transform="translate(306.057 112.41)"
														fill="#009946"
													/>
													<path
														id="Path_487"
														data-name="Path 487"
														d="M672.78,410.358a3.845,3.845,0,0,0,.282-.93v-2.414c0-1.177.519-1.661,1.72-1.673h2.956c.895,0,1.248.354,1.259,1.261s0,2.084,0,3.133a1.354,1.354,0,0,1-1.484,1.518h-2.355a7.307,7.307,0,0,0-1.177.283v.342a11.155,11.155,0,0,0,1.319.271h2.237a1.349,1.349,0,0,1,1.068.392,1.359,1.359,0,0,1,.392,1.068v3.227c0,.883-.388,1.177-1.3,1.236h-3.05c-1.049,0-1.508-.447-1.579-1.519V414.2a7.686,7.686,0,0,0-.247-1.177h-.353a5.321,5.321,0,0,0-.283,1.1v2.355a1.426,1.426,0,0,1-1.637,1.567H667.6c-.965,0-1.307-.342-1.319-1.284s0-2.026,0-3.038a1.441,1.441,0,0,1,1.59-1.613h2.426a5.294,5.294,0,0,0,1-.283v-.329a7.548,7.548,0,0,0-1.179-.248c-.823,0-1.672,0-2.5-.058a1.31,1.31,0,0,1-1.3-.9,1.326,1.326,0,0,1-.055-.559V406.5a1.09,1.09,0,0,1,.06-.467,1.081,1.081,0,0,1,1.117-.71h3.039c1.177,0,1.614.471,1.66,1.625V409.3a5.718,5.718,0,0,0,.259,1.048Z"
														transform="translate(310.599 112.408)"
														fill="#009946"
													/>
												</g>
											</svg>
										</Link>
									</div>
									<div className="jumo single-item-brand">
										<Link
											href={'/ready-stock?brand=jumo'}
											target={'_blank'}
											onMouseEnter={jumoEnter}
											onMouseLeave={jumoLeave}
											className={'Jumo_Logo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="124.626"
												height="124.626"
												viewBox="0 0 124.626 124.626">
												<g
													id="Jumo_Logo"
													transform="translate(-820 -329.5)">
													<path
														id="Path_469"
														data-name="Path 469"
														d="M578.313,345.626A62.313,62.313,0,1,0,516,283.313,62.313,62.313,0,0,0,578.313,345.626Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<g
														id="Jumo_logo_1-01"
														data-name="Jumo logo_1-01"
														transform="translate(831.991 375.348)">
														<path
															id="Path_775"
															data-name="Path 775"
															d="M84.388,36.719h-63.6A16.6,16.6,0,0,1,4.21,20.141v-.064A16.6,16.6,0,0,1,20.788,3.5h63.6a16.6,16.6,0,0,1,16.578,16.578v.064A16.6,16.6,0,0,1,84.388,36.719Zm-63.6-31.6A14.977,14.977,0,0,0,5.826,20.073v.064A14.978,14.978,0,0,0,20.788,35.1h63.6A14.978,14.978,0,0,0,99.35,20.143v-.064A14.973,14.973,0,0,0,84.388,5.124Z"
															transform="translate(-4.21 -3.5)"
															fill="#004489"
														/>
														<path
															id="Path_776"
															data-name="Path 776"
															d="M100.213,67.881l-.025,13.753c.025,2.765-2.729,4.551-5.167,4.746a17.355,17.355,0,0,1-2.972-.024,10.127,10.127,0,0,1-5.508-2.912V76.052s1.855,1.542,2.023,1.739a15.015,15.015,0,0,0,1.927,1.225,2.217,2.217,0,0,0,1.34.268c.8.025,1.072-.489,1.072-1.444V67.928l7.312-.048h0Z"
															transform="translate(-76.996 -60.417)"
															fill="#004489"
															fillRule="evenodd"
														/>
														<path
															id="Path_777"
															data-name="Path 777"
															d="M231.46,67.911s.024,7.513,0,10.988c0,1.786,4.266,1.713,4.193-.026l.024-11.013,6.872.024.023,12.553c-.048,6.26-6.471,5.889-6.626,5.89-.963,0-1.878.01-2.757.012s-1.66,0-2.447-.012c-.427-.006-6.2.595-6.253-5.84-.048-4.65,0-12.626,0-12.626l6.972.048h0Z"
															transform="translate(-198.934 -60.399)"
															fill="#004489"
															fillRule="evenodd"
														/>
														<path
															id="Path_778"
															data-name="Path 778"
															d="M408.018,67.949s2.926,4.428,4.338,6.705c.073.024,4.459-6.558,4.556-6.724h5.825V86.3l-7-.025V80.87l-3.362,5.505h0c-.073-.073-3.362-5.555-3.362-5.555s.072,3.942.023,5.457c-2,.025-6.922,0-6.922,0V67.949Z"
															transform="translate(-355.991 -60.461)"
															fill="#004489"
															fillRule="evenodd"
														/>
														<path
															id="Path_779"
															data-name="Path 779"
															d="M609.34,64.48c9.456,0,8.846,5.8,8.846,9.593,0,5.162.78,9.569-9.017,9.569-9.854.024-9.115-4.724-9.14-9.227-.024-4.843-.512-9.934,9.31-9.934h0Zm-2.266,9.566c0,1.616-.195,4.357,2.145,4.357,2.487,0,2.192-2.446,2.192-4.405,0-2.055.348-4.454-2.241-4.454C606.83,69.544,607.073,72.674,607.073,74.046Z"
															transform="translate(-530.948 -57.411)"
															fill="#004489"
															fillRule="evenodd"
														/>
													</g>
												</g>
											</svg>
										</Link>
									</div>
									<div className="yokogawa single-item-brand">
										<Link
											href={'/ready-stock?brand=phoenix'}
											target={'_blank'}
											onMouseEnter={phoenixEnter}
											onMouseLeave={phoenixLeave}
											className={'Phoenix_Logo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="115.94"
												height="115.94"
												viewBox="0 0 115.94 115.94">
												<g
													id="Phoenix_Logo"
													transform="translate(-594 -143.5)">
													<path
														id="Path_271"
														data-name="Path 271"
														d="M347.97,150.94A57.97,57.97,0,1,0,290,92.97,57.97,57.97,0,0,0,347.97,150.94Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<path
														id="Path_272"
														data-name="Path 272"
														d="M306.048,92.859V84.793c0-1.733.377-2.139,2.069-2.139h16.257c1.635,0,2,.391,2.013,2.055v16.523c0,1.706-.392,2.083-2.139,2.083H308.117c-1.706,0-2.069-.378-2.069-2.125C306.034,98.408,306.048,95.641,306.048,92.859Zm2.18,0v6.864c0,.993.434,1.482,1.4,1.5h4.879c.95,0,1.4-.5,1.244-1.635a10.812,10.812,0,0,1,0-2.8,2.573,2.573,0,0,0-.979-2.8,2.922,2.922,0,0,1-.167-4.781,3.2,3.2,0,0,0,1.2-3.047c0-1.062-.489-1.538-1.524-1.524H309.92c-1.454,0-1.72.545-1.692,1.747.042,2.195,0,4.347,0,6.5Zm9.157,2.474a2.554,2.554,0,0,0-.2,1.733c.237,1.021.531,1.3,1.4,1.272a6.878,6.878,0,0,0,6.444-6.766,6.808,6.808,0,0,0-6.291-6.892,1.915,1.915,0,0,0-1.4.671,3.16,3.16,0,0,0,.754,3.62,3.047,3.047,0,0,1,.126,4.976,4.287,4.287,0,0,0-.838,1.412Zm-.993-2.237a1.69,1.69,0,0,0,1.7-1.621,1.607,1.607,0,0,0-1.733-1.58,1.484,1.484,0,0,0-1.468,1.677,1.51,1.51,0,0,0,1.5,1.552Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_273"
														data-name="Path 273"
														d="M361.865,82.64v2.153h-3.928l-.14,1.4h3.886v2.321h-3.788v1.4h3.942v2.139c-3.467,0-6.892.21-10.288,0a4.544,4.544,0,0,1-3.789-4.865,4.655,4.655,0,0,1,4.194-4.515C355.225,82.444,358.482,82.64,361.865,82.64ZM352.918,85a2.222,2.222,0,0,0-1.859,2.474,2.18,2.18,0,0,0,1.9,2.306,2.122,2.122,0,0,0,1.915-2.39,2.152,2.152,0,0,0-1.957-2.418Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_274"
														data-name="Path 274"
														d="M389.906,95.948l-2.432.126v7.157h-3.355V96.046c-1.4,0-2.684-.1-3.942,0a2.391,2.391,0,0,0-2,2.572,2.321,2.321,0,0,0,2.251,2.32,13.012,13.012,0,0,0,2.4-.363c.252,2.292,0,2.642-2.418,2.8-3.1.126-5.508-1.8-5.592-4.543a5.1,5.1,0,0,1,5.27-5.242h9.855Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_275"
														data-name="Path 275"
														d="M343.749,82.556h3.341V92.2h-3.272V88.636h-3.131V92.2H337.43V82.64h3.2v3.173h3.118Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_276"
														data-name="Path 276"
														d="M340.953,93.53a4.989,4.989,0,0,1,5.186,5,5.255,5.255,0,1,1-5.186-5Zm1.817,4.879a1.943,1.943,0,1,0-3.413,1.645,1.943,1.943,0,0,0,3.414-.78A1.928,1.928,0,0,0,342.77,98.408Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_277"
														data-name="Path 277"
														d="M335.613,96.311c-2.251-.671-3.523-.531-4.389.573a2.6,2.6,0,0,0,0,3.187c.88,1.105,2.152,1.258,3.956.49.573,2.013.2,2.586-1.846,2.8a5.174,5.174,0,0,1-6-4.193,4.574,4.574,0,0,1,2.055-4.557,5.592,5.592,0,0,1,5.41-.545C335.3,94.2,335.361,95.514,335.613,96.311Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_278"
														data-name="Path 278"
														d="M366.226,93.529v2.279l-2.418.126v7.185h-3.3V95.962l-2.558-.154V93.529Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_279"
														data-name="Path 279"
														d="M377.339,82.556v9.659h-3.271V82.556Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_280"
														data-name="Path 280"
														d="M336.871,85.855a3.55,3.55,0,0,1-3.788,3.313h-1.622V92.2h-2.474V82.556h4.1a3.538,3.538,0,0,1,3.788,3.3Zm-2.474,0a1.263,1.263,0,0,0-.105-.489,1.245,1.245,0,0,0-1.209-.742h-1.622V87.1h1.622a1.244,1.244,0,0,0,1.314-1.244Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_281"
														data-name="Path 281"
														d="M356.637,93.529v9.59H354.5l-4.683-5.2v5.2h-2.8v-9.59H349.1l4.683,5.214V93.529Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_282"
														data-name="Path 282"
														d="M372.768,82.556v9.589h-2.083l-4.7-5.214v5.214h-2.8V82.556h2.083l4.7,5.2v-5.2Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_283"
														data-name="Path 283"
														d="M385.978,92.551l-2.124-2.922-2.125,2.88h-3.257l3.746-5.13-3.565-4.865h3.257l1.944,2.684,1.929-2.642h3.271l-3.579,4.865,3.76,5.13Z"
														transform="translate(304 108.5)"
													/>
													<path
														id="Path_284"
														data-name="Path 284"
														d="M372.279,101.623h-3.97l-.531,1.5h-2.67l3.634-9.59h3.1l3.634,9.59h-2.67Zm-.727-2.069-1.258-3.509-1.244,3.509Z"
														transform="translate(304 108.5)"
													/>
												</g>
											</svg>
										</Link>
									</div>
									<div className="beamex single-item-brand">
										<Link
											href={'/ready-stock?brand=baumer'}
											target={'_blank'}
											onMouseEnter={baumerEnter}
											onMouseLeave={baumerLeave}
											className={'Baumer_Logo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="119.1"
												height="119.1"
												viewBox="0 0 119.1 119.1">
												<g
													id="Baumer_Logo"
													transform="translate(-881 -202.5)">
													<path
														id="Path_285"
														data-name="Path 285"
														d="M636.55,213.1A59.55,59.55,0,1,0,577,153.55,59.549,59.549,0,0,0,636.55,213.1Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<path
														id="Path_286"
														data-name="Path 286"
														d="M601.836,152.8l.439-.5-.451-.5a.719.719,0,0,1-.691.452.779.779,0,0,1-.477-.251.655.655,0,0,1,0-.891c.339-.3.615-.239,1.13.251l.439-.477-.439-.49c-.188.251-.4.5-.741.44a.944.944,0,0,1-.464-.277.553.553,0,0,1,0-.665,2.912,2.912,0,0,1,.49-.464.69.69,0,0,1-.542-.546.7.7,0,0,1,0-.27.591.591,0,0,1,.439-.489.692.692,0,0,1,.854.426l.464-.5-.452-.5c-.176.264-.389.5-.753.427a.933.933,0,0,1-.414-.213.692.692,0,0,1,0-.9c.314-.3.653-.226,1.155.239l.426-.477-.452-.5a.728.728,0,0,1-.878.4.589.589,0,0,1-.427-.527.74.74,0,0,1,.578-.79c-.352-.164-.628-.339-.578-.728a.846.846,0,0,1,.163-.427.514.514,0,0,1,.628-.188,3.088,3.088,0,0,1,.552.376l.414-.452-.452-.5-.138.2a.677.677,0,0,1-.264.194.69.69,0,0,1-.324.046.676.676,0,0,1-.529-.353c-.1-.2,0-.3.164-.3h4.405c.238,0,.226.138.226.3V154.14c0,.226-.063.3-.3.3h-4.53a.549.549,0,0,1,.364-.6c.326-.138.564,0,.941.376l.439-.477-.452-.5a.69.69,0,0,1-.753.427.825.825,0,0,1-.414-.213.692.692,0,0,1,.076-.942C601.071,152.22,601.422,152.307,601.836,152.8Zm0-3.765-.577.464.565.465.426-.465Zm0-3.338.414-.465-.426-.464-.528.452Zm1.77,3.765-.364-.364-.389.376.389.364Zm-.364-2.51.351-.377-.339-.364-.4.389Zm0,5.685.351-.377-.339-.364-.4.377Zm-.389-8.886.389.364.351-.376-.339-.364Zm.74,1.406-.339-.364-.4.389.389.351Zm-.74,2.861.389.364.339-.376-.327-.364Zm.728,5.673-.327-.377-.4.39.389.351Zm-.352-3.188-.376.352.376.351c.452-.4.465-.263.025-.653Zm1.092-1.066a1.188,1.188,0,0,0,.376.213s.2-.138.189-.2a1.066,1.066,0,0,0-.214-.314Zm.063-1.444c.125.076.15.339.364.189s.2-.176,0-.327-.2.088-.339.188Zm0-1.43.339.3c.075-.126.175-.214.188-.314s-.138-.213-.188-.2a1.155,1.155,0,0,0-.352.239Zm0-2.812a1.235,1.235,0,0,0,.339.2s.188-.138.188-.2-.126-.188-.2-.3Zm0,8.522c.15,0,.163.376.414.15s.063-.238,0-.326-.264.126-.389.2Zm0-7.1a1.15,1.15,0,0,0,.339.213s.188-.138.175-.2a1.952,1.952,0,0,0-.188-.326Zm0,8.508c.113.076.138.339.351.2s.188-.188,0-.339-.188.1-.314.164Zm0-2.823.3.251c.3-.226.3-.314,0-.5Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_287"
														data-name="Path 287"
														d="M610.521,150.425l-.4.452.451.515c.164-.264.364-.5.691-.465a.791.791,0,0,1,.464.239.639.639,0,0,1,0,.9c-.351.314-.665.251-1.129-.263l-.44.5.452.489a.689.689,0,0,1,.7-.439.933.933,0,0,1,.477.251.516.516,0,0,1,.168.308.51.51,0,0,1-.068.345,2.981,2.981,0,0,1-.477.5.7.7,0,0,1,.527.665h-4.568c-.213,0-.288,0-.288-.276V143.347c0-.238.062-.3.3-.3h4.555c0,.376-.251.54-.6.69.351.151.6.352.615.7s-.213.552-.577.7c.326.175.589.364.539.728a.913.913,0,0,1-.188.439c-.314.339-.7.251-1.129-.226l-.44.489.452.515a.689.689,0,0,1,.8-.439.919.919,0,0,1,.414.276c.276.364.138.678-.451,1.092.326.138.564.326.552.715s-.264.54-.615.678c.3.15.6.276.6.64a1.094,1.094,0,0,1-.138.489.55.55,0,0,1-.652.226A3.965,3.965,0,0,1,610.521,150.425Zm.59-.966-.577-.464-.414.464.414.464Zm-.577-6.15-.414.465.414.464.577-.452Zm.577,10.417-.577-.477-.414.477.414.464Zm-.979-5.685.414.464.553-.464-.553-.477Zm.941-2.849-.514-.477-.427.477.427.477Zm-1.945-.377-.339.364.352.377.389-.364Zm0,7.844.389-.364-.389-.376-.339.364Zm-.339-8.9.352.364.389-.351-.414-.377Zm0,5.685.377.364.389-.351-.352-.314Zm.778,1.431-.414-.364-.326.364.339.364Zm-.4,3.2.389-.364-.414-.364-.327.352Zm-.352-7.53.352.389.376-.364-.389-.376Zm.741,1.456-.4-.376-.339.351.339.377Zm-1.832,5.384c-.076.126-.176.213-.189.326s.138.2.189.189a1.609,1.609,0,0,0,.338-.226Zm0-7.944c.1-.126.251-.226.238-.3s-.163-.163-.251-.251-.213.163-.2.251a2.351,2.351,0,0,0,.2.389Zm.3,3.978-.151-.15-.15-.126c-.314.251-.314.3,0,.527Zm0-2.874a1.389,1.389,0,0,0-.352-.213s-.188.138-.175.2a.826.826,0,0,0,.2.314Zm-.327-3.15c-.1.163-.188.251-.2.352s.138.2.188.2a1.526,1.526,0,0,0,.339-.226Zm0,8.559c-.1.126-.225.214-.225.3s.15.238.2.226a.377.377,0,0,0,.251-.226c.038-.038-.1-.138-.2-.264Zm0-4.229c-.326.213-.326.3,0,.5l.277-.251Zm0,2.861c-.326.226-.326.3,0,.5l.277-.251Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_288"
														data-name="Path 288"
														d="M636.989,148.593l.214.063a2.511,2.511,0,0,1,1.857,2.385,3.427,3.427,0,0,1-.351,1.832,2.791,2.791,0,0,1-2.159,1.418,17.067,17.067,0,0,1-1.795.088h-1.782c-.176,0-.263-.063-.238-.239V143.523c0-.364,0-.364.376-.364h2.711a3.342,3.342,0,0,1,1.544.339,2.506,2.506,0,0,1,1.4,2.159,2.794,2.794,0,0,1-.175,1.43,2.51,2.51,0,0,1-1.343,1.255l-.276.113Zm-2.8,2.51V152.9c0,.125,0,.238.164.238h1.782a1.53,1.53,0,0,0,1.38-1.393,4.576,4.576,0,0,0,0-.828,1.819,1.819,0,0,0-1.581-1.77h-1.544c-.175,0-.251.076-.251.251.05.553.05,1.155.05,1.757Zm0-5.02v1.594c0,.213.063.3.277.289h1.1a1.684,1.684,0,0,0,1.707-1.431,2.856,2.856,0,0,0,0-.653,1.583,1.583,0,0,0-1.456-1.619H634.4c-.2,0-.276.088-.264.289a14.036,14.036,0,0,1,.05,1.607Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_289"
														data-name="Path 289"
														d="M657.822,147.2a2.285,2.285,0,0,1,2.02-1.368,1.958,1.958,0,0,1,1.971,1.406l.213-.264a2.4,2.4,0,0,1,1.368-1.079,1.97,1.97,0,0,1,2.6,1.707,5.813,5.813,0,0,1,.075,1.054v5.421c0,.314,0,.314-.313.314a7.12,7.12,0,0,0-.716,0c-.2,0-.251-.088-.251-.276v-4.957a7.561,7.561,0,0,0,0-.929,1.916,1.916,0,0,0-.138-.577,1.139,1.139,0,0,0-.785-.677,1.146,1.146,0,0,0-1.01.238,2.283,2.283,0,0,0-.828,1.844v5.02c0,.327,0,.327-.339.327h-.615c-.364,0-.364,0-.364-.352v-5.1a4.124,4.124,0,0,0-.125-1.117,1.142,1.142,0,0,0-.283-.537,1.141,1.141,0,0,0-1.126-.321,1.139,1.139,0,0,0-.524.306,2.382,2.382,0,0,0-.728,1.782v5.02c0,.226,0,.326-.288.314a5.491,5.491,0,0,0-.791,0c-.226,0-.289-.076-.276-.289v-6.074a12.083,12.083,0,0,0-.088-1.807c0-.164,0-.226.2-.226h.878c.289,0,.226,0,.226.213a6.6,6.6,0,0,0,0,.69,1.758,1.758,0,0,0,0,.251Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_290"
														data-name="Path 290"
														d="M645,153.224l-.314.414a2.509,2.509,0,0,1-3.489.364,2.348,2.348,0,0,1-.778-2.121,2.622,2.622,0,0,1,2.184-2.334,6.855,6.855,0,0,1,2.133-.239c.314,0,.314,0,.327-.326a2.957,2.957,0,0,0-.276-1.431,1.263,1.263,0,0,0-.829-.64,3.039,3.039,0,0,0-2.284.4l-.489.289c0-.377-.076-.716-.088-1.055a.234.234,0,0,1,.032-.092.22.22,0,0,1,.068-.071,5.019,5.019,0,0,1,3.426-.477,2.159,2.159,0,0,1,1.7,1.845,4.93,4.93,0,0,1,.088.954v5.334c0,.2,0,.288-.251.276h-.816c-.163,0-.264,0-.264-.226v-.878Zm.1-2.4v-.276c0-.276,0-.3-.3-.289a9.757,9.757,0,0,0-1.255.088,1.93,1.93,0,0,0-1.606,1.1,1.554,1.554,0,0,0,.175,1.681,1.421,1.421,0,0,0,1.532.427,1.761,1.761,0,0,0,1.255-1.381C645.009,151.693,645.034,151.254,645.1,150.827Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_291"
														data-name="Path 291"
														d="M653.015,153.211l-.225.314a2.323,2.323,0,0,1-3.012.8,2.21,2.21,0,0,1-1.143-1.769,5.155,5.155,0,0,1,0-.879v-5.346c0-.326,0-.352.364-.352h.6c.364,0,.4,0,.4.389v5.435a2.133,2.133,0,0,0,0,.564,1.255,1.255,0,0,0,.979,1.08,1.423,1.423,0,0,0,1.469-.527,2.414,2.414,0,0,0,.489-1.506v-5.1c0-.238.075-.313.3-.288h.816c.213,0,.276,0,.276.263v7.919c0,.138,0,.189-.175.189H653.3c-.163,0-.226-.063-.213-.214s0-.452,0-.678a1.888,1.888,0,0,0,0-.238Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_292"
														data-name="Path 292"
														d="M673.535,152.973A5.707,5.707,0,0,0,673.6,154a.162.162,0,0,1-.02.089.156.156,0,0,1-.068.062,4.248,4.248,0,0,1-3.991-.088,3.2,3.2,0,0,1-1.406-2.259,6.81,6.81,0,0,1,.326-4.205,2.764,2.764,0,0,1,2.084-1.757,2.51,2.51,0,0,1,2.836,1.444,5.215,5.215,0,0,1,.5,2.045v.992c0,.339,0,.188-.2.188h-4.166c-.113,0-.214,0-.214.151a3.094,3.094,0,0,0,.879,2.372,2.126,2.126,0,0,0,2.045.389A11.328,11.328,0,0,0,673.535,152.973Zm-2.51-3.351h1.368c.113,0,.238,0,.238-.176a3.851,3.851,0,0,0-.427-1.907,1.245,1.245,0,0,0-.765-.665,1.342,1.342,0,0,0-1.544.677,3.886,3.886,0,0,0-.489,1.858c0,.175.1.213.251.213Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_293"
														data-name="Path 293"
														d="M676.911,147.275l.188-.313a2.095,2.095,0,0,1,1.907-1.13c.264,0,.289,0,.264.3s-.063.665-.088,1h-.226a1.631,1.631,0,0,0-1.606.929,3.164,3.164,0,0,0-.389,1.519v4.53c0,.226-.076.289-.289.276h-.841c-.175,0-.238,0-.238-.238v-7.882c0-.226.075-.3.288-.288h.728c.151,0,.226,0,.226.213v.8a1.512,1.512,0,0,1,0,.251Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_294"
														data-name="Path 294"
														d="M612.642,152.182a.6.6,0,0,1-.232-.047.592.592,0,0,1-.194-.134.594.594,0,0,1-.164-.434.628.628,0,0,1,1.255,0,.575.575,0,0,1-.043.253.579.579,0,0,1-.367.339A.564.564,0,0,1,612.642,152.182Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_295"
														data-name="Path 295"
														d="M599.728,153.6a.6.6,0,0,1-.229-.046.588.588,0,0,1-.192-.132.572.572,0,0,1-.127-.2.6.6,0,0,1,.342-.779.592.592,0,0,1,.642.139.565.565,0,0,1-.009.833A.627.627,0,0,1,599.728,153.6Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_296"
														data-name="Path 296"
														d="M612.642,150.752a.59.59,0,0,1-.123-1.167.59.59,0,0,1,.34.029.584.584,0,0,1,.267.211.608.608,0,0,1,.068.556.568.568,0,0,1-.321.328A.571.571,0,0,1,612.642,150.752Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_297"
														data-name="Path 297"
														d="M599.741,150.751a.579.579,0,0,1-.226-.035.607.607,0,0,1-.343-.765.584.584,0,0,1,.126-.2.562.562,0,0,1,.2-.134.59.59,0,0,1,.234-.044.635.635,0,0,1,.232.041.6.6,0,0,1,.2.127.6.6,0,0,1,.185.422.578.578,0,0,1-.046.229.588.588,0,0,1-.327.32A.581.581,0,0,1,599.741,150.751Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_298"
														data-name="Path 298"
														d="M599.138,145.9a.59.59,0,0,1,.37-.56.58.58,0,0,1,.233-.042.626.626,0,0,1,.6.6.651.651,0,0,1-.6.6.623.623,0,0,1-.419-.183A.631.631,0,0,1,599.138,145.9Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_299"
														data-name="Path 299"
														d="M599.728,145.066a.6.6,0,0,1-.232-.047.592.592,0,0,1-.194-.134.594.594,0,0,1-.164-.434.616.616,0,0,1,.59-.59.6.6,0,0,1,0,1.205Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_300"
														data-name="Path 300"
														d="M612.642,147.89a.58.58,0,0,1-.229-.041.59.59,0,0,1-.373-.549.6.6,0,0,1,.6-.514.576.576,0,0,1,.229.036.583.583,0,0,1,.328.314.564.564,0,0,1-.557.754Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_301"
														data-name="Path 301"
														d="M612.642,153.6a.615.615,0,0,1-.615-.589.6.6,0,0,1,.6-.6.627.627,0,1,1,0,1.255Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_302"
														data-name="Path 302"
														d="M599.728,152.182a.6.6,0,0,1-.229-.046.585.585,0,0,1-.192-.131.576.576,0,0,1-.127-.2.6.6,0,0,1-.042-.229.628.628,0,1,1,.836.568A.641.641,0,0,1,599.728,152.182Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_303"
														data-name="Path 303"
														d="M599.138,148.756a.6.6,0,0,1,.042-.23.586.586,0,0,1,.127-.2.586.586,0,0,1,.421-.177.584.584,0,0,1,.427,1.013.632.632,0,0,1-.427.18.616.616,0,0,1-.59-.59Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_304"
														data-name="Path 304"
														d="M612.052,148.743a.588.588,0,0,1,.182-.426.58.58,0,0,1,.2-.126.6.6,0,0,1,.233-.038.6.6,0,0,1,.59.59.628.628,0,1,1-1.255,0Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_305"
														data-name="Path 305"
														d="M612.642,145.066a.615.615,0,0,1-.6-.6.6.6,0,0,1,1.205-.013.615.615,0,0,1-.6.615Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_306"
														data-name="Path 306"
														d="M599.741,147.89a.58.58,0,0,1-.226-.036.559.559,0,0,1-.194-.12.572.572,0,0,1-.133-.186.61.61,0,0,1-.011-.458.581.581,0,0,1,.13-.2.563.563,0,0,1,.2-.13.692.692,0,0,1,.706.1.587.587,0,0,1,.2.446.589.589,0,0,1-.425.567A.6.6,0,0,1,599.741,147.89Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_307"
														data-name="Path 307"
														d="M612.052,145.882a.615.615,0,0,1,.59-.589.6.6,0,0,1,.43.167.588.588,0,0,1,.134.193.6.6,0,0,1,.051.229.626.626,0,0,1-.59.615.64.64,0,0,1-.615-.615Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_308"
														data-name="Path 308"
														d="M598.31,146.786a.564.564,0,0,1,.552.564.552.552,0,0,1-.552.54.59.59,0,0,1-.565-.552.565.565,0,0,1,.565-.552Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_309"
														data-name="Path 309"
														d="M598.31,146.447a.565.565,0,0,1-.565-.552.589.589,0,0,1,.577-.552.576.576,0,0,1,.54.577.543.543,0,0,1-.046.206.54.54,0,0,1-.3.285A.523.523,0,0,1,598.31,146.447Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_310"
														data-name="Path 310"
														d="M598.322,153.562a.556.556,0,0,1-.217-.038.577.577,0,0,1-.186-.118.584.584,0,0,1-.126-.181.57.57,0,0,1-.048-.215.559.559,0,0,1,1.117,0,.576.576,0,0,1-.54.552Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_311"
														data-name="Path 311"
														d="M598.837,148.744a.552.552,0,0,1-.539.552.578.578,0,0,1-.578-.54.565.565,0,0,1,1.117,0Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_312"
														data-name="Path 312"
														d="M598.31,150.7a.565.565,0,0,1-.383-.147.572.572,0,0,1-.182-.368.579.579,0,0,1,.16-.387.572.572,0,0,1,.38-.177.534.534,0,0,1,.213.032.566.566,0,0,1,.185.112.545.545,0,0,1,.179.383.522.522,0,0,1-.03.218.533.533,0,0,1-.119.185.518.518,0,0,1-.185.118A.507.507,0,0,1,598.31,150.7Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_313"
														data-name="Path 313"
														d="M614.625,153.061a.539.539,0,0,1-.54.54.552.552,0,0,1-.514-.35.543.543,0,0,1-.038-.215.589.589,0,0,1,.552-.552.564.564,0,0,1,.54.577Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_314"
														data-name="Path 314"
														d="M613.533,147.326a.563.563,0,0,1,.564-.553.553.553,0,1,1,0,1.105.558.558,0,0,1-.4-.157.552.552,0,0,1-.166-.395Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_315"
														data-name="Path 315"
														d="M614.625,145.9a.53.53,0,0,1-.033.209.522.522,0,0,1-.113.18.515.515,0,0,1-.174.121.529.529,0,0,1-.208.042.54.54,0,0,1-.214-.033.533.533,0,0,1-.183-.116.538.538,0,0,1-.124-.178.532.532,0,0,1-.043-.212.576.576,0,0,1,.552-.565.564.564,0,0,1,.54.552Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_316"
														data-name="Path 316"
														d="M598.322,152.132a.495.495,0,1,1,.54-.54.539.539,0,0,1-.54.54Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_317"
														data-name="Path 317"
														d="M598.3,145.028a.552.552,0,0,1-.54-.539.55.55,0,0,1,.162-.391.539.539,0,0,1,.179-.119.553.553,0,0,1,.423,0,.539.539,0,0,1,.179.119.55.55,0,0,1,.162.391.564.564,0,0,1-.047.21.556.556,0,0,1-.124.177.541.541,0,0,1-.182.115A.548.548,0,0,1,598.3,145.028Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_318"
														data-name="Path 318"
														d="M614.625,150.161a.515.515,0,0,1-.029.21.5.5,0,0,1-.289.3.5.5,0,0,1-.209.034.514.514,0,0,1-.213-.024.5.5,0,0,1-.184-.108.512.512,0,0,1-.125-.173.518.518,0,0,1-.043-.209.551.551,0,0,1,.038-.216.567.567,0,0,1,.119-.183.534.534,0,0,1,.181-.123.542.542,0,0,1,.214-.043.518.518,0,0,1,.385.154.511.511,0,0,1,.117.177A.529.529,0,0,1,614.625,150.161Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_319"
														data-name="Path 319"
														d="M614.625,151.58a.527.527,0,0,1-.527.552.54.54,0,1,1,0-1.079.485.485,0,0,1,.206.033.495.495,0,0,1,.288.288A.5.5,0,0,1,614.625,151.58Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_320"
														data-name="Path 320"
														d="M614.072,145.016a.539.539,0,1,1,.382-.158A.539.539,0,0,1,614.072,145.016Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_321"
														data-name="Path 321"
														d="M614.1,149.3a.552.552,0,0,1-.564-.527.576.576,0,0,1,.552-.54.533.533,0,0,1,.385.154.535.535,0,0,1,.117.177.553.553,0,0,1,.038.209.518.518,0,0,1-.033.206.5.5,0,0,1-.289.288A.5.5,0,0,1,614.1,149.3Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_322"
														data-name="Path 322"
														d="M615.516,150.651a.5.5,0,0,1-.191-.034.491.491,0,0,1-.311-.456.5.5,0,0,1,.037-.187.487.487,0,0,1,.265-.265.5.5,0,0,1,.188-.037.5.5,0,0,1,.187.037.487.487,0,0,1,.265.265.5.5,0,0,1,.037.187.462.462,0,0,1-.033.187.471.471,0,0,1-.258.265A.476.476,0,0,1,615.516,150.651Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_323"
														data-name="Path 323"
														d="M615.516,151.1a.476.476,0,0,1,.186.038.471.471,0,0,1,.258.265.462.462,0,0,1,.033.187.49.49,0,0,1-.979,0,.489.489,0,0,1,.311-.456A.5.5,0,0,1,615.516,151.1Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_324"
														data-name="Path 324"
														d="M596.4,144.488a.464.464,0,0,1,.127-.344.468.468,0,0,1,.337-.145.515.515,0,0,1,.515.489.49.49,0,0,1-.489.477.471.471,0,0,1-.346-.135.464.464,0,0,1-.106-.156A.476.476,0,0,1,596.4,144.488Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_325"
														data-name="Path 325"
														d="M616.005,144.476a.479.479,0,0,1-.032.187.474.474,0,0,1-.1.159.464.464,0,0,1-.156.106.476.476,0,0,1-.186.038.492.492,0,0,1-.209-.012.485.485,0,0,1-.186-.1.5.5,0,0,1-.127-.167.492.492,0,0,1,0-.411.5.5,0,0,1,.127-.167.485.485,0,0,1,.186-.1.492.492,0,0,1,.209-.012.476.476,0,0,1,.477.477Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_326"
														data-name="Path 326"
														d="M596.4,151.6a.482.482,0,0,1,.126-.349.47.47,0,0,1,.154-.111.495.495,0,1,1-.28.46Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_327"
														data-name="Path 327"
														d="M615.528,145.43a.46.46,0,0,1,.191.035.454.454,0,0,1,.16.11.465.465,0,0,1,.126.357.489.489,0,0,1-.5.465.529.529,0,0,1-.477-.5.473.473,0,0,1,.043-.185.478.478,0,0,1,.11-.154.488.488,0,0,1,.162-.1A.479.479,0,0,1,615.528,145.43Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_328"
														data-name="Path 328"
														d="M596.879,150.651a.476.476,0,0,1-.186-.038.471.471,0,0,1-.258-.265.463.463,0,0,1-.033-.187.458.458,0,0,1,.033-.186.477.477,0,0,1,.258-.266.494.494,0,0,1,.186-.037.539.539,0,0,1,.5.514.5.5,0,0,1-.5.465Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_329"
														data-name="Path 329"
														d="M596.4,145.92a.467.467,0,0,1,.03-.192.458.458,0,0,1,.1-.162.488.488,0,0,1,.163-.106.481.481,0,0,1,.192-.03.5.5,0,0,1,.489.477.526.526,0,0,1-.489.49.49.49,0,0,1-.49-.477Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_330"
														data-name="Path 330"
														d="M615.491,149.22a.477.477,0,0,1-.477-.464.514.514,0,0,1,.5-.5.483.483,0,0,1,.19.043.5.5,0,0,1,.157.113.48.48,0,0,1,.1.166.485.485,0,0,1,.029.192.458.458,0,0,1-.042.183.48.48,0,0,1-.112.152.462.462,0,0,1-.162.094A.452.452,0,0,1,615.491,149.22Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_331"
														data-name="Path 331"
														d="M615.516,152.534a.464.464,0,0,1,.477.477.457.457,0,0,1-.033.186.477.477,0,0,1-.258.266.494.494,0,0,1-.186.037.5.5,0,0,1-.5-.477.493.493,0,0,1,.148-.35.488.488,0,0,1,.163-.106A.5.5,0,0,1,615.516,152.534Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_332"
														data-name="Path 332"
														d="M596.892,149.22a.463.463,0,0,1-.192-.029.488.488,0,0,1-.163-.106.454.454,0,0,1-.1-.163.462.462,0,0,1-.03-.191.467.467,0,0,1,.038-.185.478.478,0,0,1,.106-.157.485.485,0,0,1,.159-.1.462.462,0,0,1,.187-.033.552.552,0,0,1,.489.5.489.489,0,0,1-.489.464Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_333"
														data-name="Path 333"
														d="M596.892,147.8a.478.478,0,0,1-.183-.036.488.488,0,0,1-.258-.258.478.478,0,0,1-.036-.183.473.473,0,0,1,.14-.337.487.487,0,0,1,.154-.1.478.478,0,0,1,.183-.036.469.469,0,0,1,.182.036.483.483,0,0,1,.259.259.469.469,0,0,1,.036.182.478.478,0,0,1-.036.183.487.487,0,0,1-.1.154.473.473,0,0,1-.337.14Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_334"
														data-name="Path 334"
														d="M596.879,153.487a.466.466,0,0,1-.346-.14.454.454,0,0,1-.1-.161A.463.463,0,0,1,596.4,153a.456.456,0,0,1,.034-.186.44.44,0,0,1,.107-.156.447.447,0,0,1,.161-.1.46.46,0,0,1,.188-.024.47.47,0,0,1,.447.664.514.514,0,0,1-.11.156.5.5,0,0,1-.162.1A.484.484,0,0,1,596.879,153.487Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_335"
														data-name="Path 335"
														d="M615.993,147.325a.477.477,0,0,1-.038.186.491.491,0,0,1-.106.156.506.506,0,0,1-.159.1.517.517,0,0,1-.369,0,.465.465,0,0,1-.155-.1.473.473,0,0,1-.1-.154.478.478,0,0,1,.44-.66.466.466,0,0,1,.35.131.462.462,0,0,1,.106.159A.477.477,0,0,1,615.993,147.325Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_336"
														data-name="Path 336"
														d="M600.33,154.441h-1.079c-.138,0-.113-.1-.075-.2a.589.589,0,0,1,1.154.213Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_337"
														data-name="Path 337"
														d="M613.257,154.441h-1.092s-.088-.125-.075-.188a.589.589,0,0,1,.627-.4C612.993,153.864,613.194,154.065,613.257,154.441Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_338"
														data-name="Path 338"
														d="M616.946,146.322a.391.391,0,0,1-.306-.117.407.407,0,0,1-.088-.143.419.419,0,0,1,.232-.557.374.374,0,0,1,.162-.024.437.437,0,0,1,.427.427.416.416,0,0,1-.264.386A.431.431,0,0,1,616.946,146.322Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_339"
														data-name="Path 339"
														d="M616.959,144.878a.38.38,0,0,1-.309-.1.379.379,0,0,1-.093-.138.372.372,0,0,1-.025-.165.428.428,0,0,1,.246-.411.416.416,0,0,1,.248-.035.427.427,0,0,1,.225.11.435.435,0,0,1,.124.218.427.427,0,0,1-.416.52Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_340"
														data-name="Path 340"
														d="M616.934,151.994a.393.393,0,0,1-.3-.122.383.383,0,0,1-.085-.141.4.4,0,0,1-.019-.164.423.423,0,0,1,.13-.293.427.427,0,0,1,.3-.121.452.452,0,0,1,.414.452.383.383,0,0,1-.038.159.393.393,0,0,1-.1.131.409.409,0,0,1-.142.081A.4.4,0,0,1,616.934,151.994Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_341"
														data-name="Path 341"
														d="M616.532,147.313a.368.368,0,0,1,.024-.161.376.376,0,0,1,.09-.136.382.382,0,0,1,.138-.086.373.373,0,0,1,.162-.018.425.425,0,0,1,.427.414.407.407,0,0,1-.033.161.4.4,0,0,1-.093.136.4.4,0,0,1-.138.089.412.412,0,0,1-.163.028.4.4,0,0,1-.163-.029.405.405,0,0,1-.138-.093.412.412,0,0,1-.089-.14A.421.421,0,0,1,616.532,147.313Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_342"
														data-name="Path 342"
														d="M595.448,144.878a.415.415,0,0,1,0-.829.44.44,0,0,1,.44.44.446.446,0,0,1-.041.156.413.413,0,0,1-.1.129.431.431,0,0,1-.14.082A.419.419,0,0,1,595.448,144.878Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_343"
														data-name="Path 343"
														d="M616.934,149.76a.416.416,0,0,1,.3.108.411.411,0,0,1,.1.133.407.407,0,0,1,.037.161.451.451,0,0,1-.414.439.412.412,0,0,1-.162-.037.427.427,0,0,1-.224-.238.417.417,0,0,1-.028-.164.348.348,0,0,1,.013-.16.344.344,0,0,1,.082-.137.352.352,0,0,1,.135-.087A.357.357,0,0,1,616.934,149.76Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_344"
														data-name="Path 344"
														d="M595.072,145.9a.414.414,0,0,1,.829,0,.406.406,0,0,1-.02.166.391.391,0,0,1-.088.143.4.4,0,0,1-.141.093.388.388,0,0,1-.166.024.392.392,0,0,1-.166-.024.4.4,0,0,1-.14-.093.391.391,0,0,1-.108-.309Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_345"
														data-name="Path 345"
														d="M595.461,151.994a.381.381,0,0,1-.164-.024.39.39,0,0,1-.141-.088.406.406,0,0,1-.093-.138.418.418,0,0,1-.029-.164.427.427,0,1,1,.854,0,.4.4,0,0,1-.029.164.4.4,0,0,1-.093.138.4.4,0,0,1-.14.088A.388.388,0,0,1,595.461,151.994Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_346"
														data-name="Path 346"
														d="M616.532,152.985a.389.389,0,0,1,.028-.158.389.389,0,0,1,.227-.22.376.376,0,0,1,.159-.023.439.439,0,0,1,.427.427.427.427,0,0,1-.841,0Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_347"
														data-name="Path 347"
														d="M616.959,149.145a.375.375,0,0,1-.166-.02.365.365,0,0,1-.143-.088.378.378,0,0,1-.093-.14.407.407,0,0,1-.025-.166.363.363,0,0,1,.019-.16.363.363,0,0,1,.223-.223.362.362,0,0,1,.16-.018.415.415,0,0,1,.179.007.41.41,0,0,1,.159.083.418.418,0,0,1,.109.142.41.41,0,0,1,.039.175.4.4,0,0,1-.039.175.408.408,0,0,1-.268.225.4.4,0,0,1-.179.008Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_348"
														data-name="Path 348"
														d="M595.875,147.326a.379.379,0,0,1-.024.163.391.391,0,0,1-.089.138.389.389,0,0,1-.3.113.41.41,0,0,1-.23-.07.414.414,0,1,1,.574-.574A.41.41,0,0,1,595.875,147.326Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_349"
														data-name="Path 349"
														d="M595.863,148.731a.409.409,0,0,1-.025.166.378.378,0,0,1-.093.14.369.369,0,0,1-.142.088.381.381,0,0,1-.167.02.415.415,0,0,1-.4-.414.433.433,0,0,1,.036-.158.437.437,0,0,1,.094-.131.411.411,0,0,1,.137-.086.438.438,0,0,1,.16-.027.362.362,0,0,1,.16.019.364.364,0,0,1,.223.222A.384.384,0,0,1,595.863,148.731Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_350"
														data-name="Path 350"
														d="M595.862,152.985a.412.412,0,0,1-.027.163.436.436,0,0,1-.089.138.417.417,0,0,1-.3.126.425.425,0,0,1-.376-.351.439.439,0,0,1,.414-.415.4.4,0,0,1,.376.339Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_351"
														data-name="Path 351"
														d="M595.448,150.551a.385.385,0,0,1-.157-.027.4.4,0,0,1-.225-.218.4.4,0,0,1-.032-.157.393.393,0,0,1,.033-.159.405.405,0,0,1,.094-.134.4.4,0,0,1,.139-.085.382.382,0,0,1,.161-.023.363.363,0,0,1,.3.1.358.358,0,0,1,.086.137.377.377,0,0,1,.019.16.406.406,0,0,1-.024.162.385.385,0,0,1-.09.136.376.376,0,0,1-.139.085A.371.371,0,0,1,595.448,150.551Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_352"
														data-name="Path 352"
														d="M612.077,143.083h1.155a.564.564,0,0,1-.552.552.556.556,0,0,1-.424-.143.568.568,0,0,1-.133-.185A.576.576,0,0,1,612.077,143.083Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_353"
														data-name="Path 353"
														d="M599.164,143.083h1.129a.527.527,0,0,1-.489.552.558.558,0,0,1-.237-.018.566.566,0,0,1-.351-.3A.572.572,0,0,1,599.164,143.083Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_354"
														data-name="Path 354"
														d="M597.757,154.416c.076-.364.276-.54.565-.54a.523.523,0,0,1,.369.164.526.526,0,0,1,.11.173.544.544,0,0,1,.036.2Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_355"
														data-name="Path 355"
														d="M614.073,153.877a.547.547,0,0,1,.313.093.54.54,0,0,1,.2.258c0,.125,0,.213-.138.213H613.7c-.138,0-.176,0-.125-.188a.562.562,0,0,1,.5-.376Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_356"
														data-name="Path 356"
														d="M598.837,143.083c0,.377-.251.527-.578.515a.529.529,0,0,1-.5-.515Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_357"
														data-name="Path 357"
														d="M614.06,143.6a.5.5,0,0,1-.489-.327c0-.125,0-.213.125-.213h.766c.138,0,.163.075.125.2a.5.5,0,0,1-.2.262A.509.509,0,0,1,614.06,143.6Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_358"
														data-name="Path 358"
														d="M618.377,145.531a.335.335,0,0,1,.137.023.343.343,0,0,1,.116.076.338.338,0,0,1,.1.252.376.376,0,0,1-.106.262.372.372,0,0,1-.258.115.35.35,0,0,1-.139-.029.339.339,0,0,1-.115-.083.353.353,0,0,1-.071-.123.359.359,0,0,1-.014-.142.339.339,0,0,1,.351-.351Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_359"
														data-name="Path 359"
														d="M618.377,146.961a.37.37,0,0,1,.142.034.365.365,0,0,1,.117.086.361.361,0,0,1,.074.125.363.363,0,0,1-.345.483.339.339,0,0,1-.14-.034.349.349,0,0,1-.115-.086.381.381,0,0,1-.07-.126.364.364,0,0,1-.014-.143.336.336,0,0,1,.027-.133.341.341,0,0,1,.077-.111.335.335,0,0,1,.247-.1Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_360"
														data-name="Path 360"
														d="M618.377,144.1a.374.374,0,0,1,.255.123.378.378,0,0,1,.1.266.365.365,0,0,1-.364.339.364.364,0,1,1,0-.728Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_361"
														data-name="Path 361"
														d="M618.729,151.555a.378.378,0,0,1-.364.376.337.337,0,0,1-.133-.027.331.331,0,0,1-.111-.077.319.319,0,0,1-.073-.114.335.335,0,0,1-.022-.133.349.349,0,0,1,.093-.265.337.337,0,0,1,.118-.083.355.355,0,0,1,.14-.029.36.36,0,0,1,.245.107A.368.368,0,0,1,618.729,151.555Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_362"
														data-name="Path 362"
														d="M618.014,148.718a.342.342,0,0,1,.031-.133.355.355,0,0,1,.08-.111.346.346,0,0,1,.117-.072.33.33,0,0,1,.136-.022.338.338,0,0,1,.14.029.344.344,0,0,1,.117.082.355.355,0,0,1,.075.123.36.36,0,0,1,.019.142.364.364,0,0,1-.59.213.36.36,0,0,1-.125-.213Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_363"
														data-name="Path 363"
														d="M594.03,149.81a.339.339,0,0,1,.352.364.313.313,0,0,1-.091.248.313.313,0,0,1-.248.091.338.338,0,0,1-.141-.019.352.352,0,0,1-.12-.075.34.34,0,0,1-.079-.118.331.331,0,0,1-.024-.139.337.337,0,0,1,.023-.137.343.343,0,0,1,.076-.116.338.338,0,0,1,.252-.1Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_364"
														data-name="Path 364"
														d="M618.365,150.551a.333.333,0,0,1-.143-.025.366.366,0,0,1-.12-.082.36.36,0,0,1-.074-.125.356.356,0,0,1-.015-.145.351.351,0,0,1,.032-.131.324.324,0,0,1,.081-.109.339.339,0,0,1,.117-.069.358.358,0,0,1,.134-.017.352.352,0,0,1,.352.352.349.349,0,0,1-.024.139.33.33,0,0,1-.08.118.333.333,0,0,1-.12.075A.352.352,0,0,1,618.365,150.551Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_365"
														data-name="Path 365"
														d="M594.03,147.689a.364.364,0,0,1,0-.728.35.35,0,0,1,.139.029.341.341,0,0,1,.115.084.336.336,0,0,1,.071.123.351.351,0,0,1,.014.141.363.363,0,0,1-.339.351Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_366"
														data-name="Path 366"
														d="M594.043,148.379a.361.361,0,0,1,.133.032.354.354,0,0,1,.112.08.381.381,0,0,1,.072.117.365.365,0,0,1,.022.135.332.332,0,0,1-.02.143.331.331,0,0,1-.2.194.342.342,0,0,1-.142.015.355.355,0,0,1-.241-.108.351.351,0,0,1-.1-.244.364.364,0,0,1,.364-.364Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_367"
														data-name="Path 367"
														d="M594.382,151.567a.349.349,0,0,1-.09.256.351.351,0,0,1-.114.08.33.33,0,0,1-.135.028.364.364,0,0,1-.364-.364.377.377,0,0,1,.339-.364.361.361,0,0,1,.257.107A.365.365,0,0,1,594.382,151.567Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_368"
														data-name="Path 368"
														d="M594.03,146.246a.364.364,0,1,1,.3-.358.364.364,0,0,1-.3.358Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_369"
														data-name="Path 369"
														d="M618.729,153.061a.378.378,0,0,1-.115.258.371.371,0,0,1-.262.1.348.348,0,0,1-.236-.115.354.354,0,0,1-.09-.248.313.313,0,0,1,.21-.338.321.321,0,0,1,.141-.014.335.335,0,0,1,.137.023.34.34,0,0,1,.192.192A.335.335,0,0,1,618.729,153.061Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_370"
														data-name="Path 370"
														d="M594.043,152.659a.328.328,0,0,1,.135.023.321.321,0,0,1,.113.077.31.31,0,0,1,.072.116.33.33,0,0,1,.019.136.365.365,0,0,1-.022.135.381.381,0,0,1-.072.117.354.354,0,0,1-.112.08.341.341,0,0,1-.133.031.374.374,0,0,1-.364-.376.335.335,0,0,1,.028-.136.32.32,0,0,1,.08-.113.341.341,0,0,1,.118-.072A.357.357,0,0,1,594.043,152.659Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_371"
														data-name="Path 371"
														d="M594.382,144.464a.365.365,0,0,1-.022.135.381.381,0,0,1-.072.117.355.355,0,0,1-.112.08.361.361,0,0,1-.133.032.389.389,0,0,1-.364-.377.359.359,0,0,1,.026-.134.361.361,0,0,1,.191-.191.359.359,0,0,1,.134-.026.343.343,0,0,1,.249.1.354.354,0,0,1,.1.248Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_372"
														data-name="Path 372"
														d="M615.039,154.441a.44.44,0,0,1,.414-.489.471.471,0,0,1,.3.061.466.466,0,0,1,.2.24s0,.176-.075.176Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_373"
														data-name="Path 373"
														d="M597.356,154.441h-.816c-.138,0-.15-.088-.113-.188a.483.483,0,0,1,.2-.237.477.477,0,0,1,.3-.064C597.168,153.977,597.306,154.115,597.356,154.441Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_374"
														data-name="Path 374"
														d="M597.356,143.021a.426.426,0,0,1-.4.476.485.485,0,0,1-.3-.047.493.493,0,0,1-.215-.216c0-.1,0-.2.1-.2Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_375"
														data-name="Path 375"
														d="M615.064,143.021h.816s.1.138.075.188a.464.464,0,0,1-.515.276.433.433,0,0,1-.283-.155A.426.426,0,0,1,615.064,143.021Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_376"
														data-name="Path 376"
														d="M592.562,144.175a.342.342,0,0,1,.314.314.33.33,0,0,1-.109.219.325.325,0,0,1-.23.082.321.321,0,0,1-.138,0,.316.316,0,0,1-.123-.061.322.322,0,0,1-.085-.109.317.317,0,0,1,0-.269.309.309,0,0,1,.085-.108.305.305,0,0,1,.123-.062.321.321,0,0,1,.138,0Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_377"
														data-name="Path 377"
														d="M592.562,153.324a.326.326,0,0,1-.215-.09.332.332,0,0,1-.1-.211.326.326,0,0,1,.326-.314.313.313,0,0,1,.261.065.309.309,0,0,1,.085.108.307.307,0,0,1,0,.269.322.322,0,0,1-.085.109.316.316,0,0,1-.123.061.293.293,0,0,1-.138,0Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_378"
														data-name="Path 378"
														d="M592.562,150.475a.321.321,0,0,1-.138,0,.316.316,0,0,1-.123-.061.322.322,0,0,1-.085-.109.312.312,0,0,1,.085-.377.3.3,0,0,1,.123-.062.435.435,0,0,1,.356.093.325.325,0,0,1,.095.218.313.313,0,0,1-.313.3Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_379"
														data-name="Path 379"
														d="M592.926,147.351a.34.34,0,0,1-.326.288.327.327,0,0,1-.211-.1.331.331,0,0,1-.091-.215.338.338,0,0,1,.327-.313.375.375,0,0,1,.207.119A.38.38,0,0,1,592.926,147.351Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_380"
														data-name="Path 380"
														d="M592.562,146.221a.324.324,0,0,1-.3-.313.3.3,0,0,1,.1-.223.293.293,0,0,1,.106-.063.3.3,0,0,1,.123-.016.321.321,0,0,1,.138,0,.3.3,0,0,1,.123.062.309.309,0,0,1,.085.108.316.316,0,0,1,0,.269.322.322,0,0,1-.085.109.316.316,0,0,1-.123.061.293.293,0,0,1-.138,0Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_381"
														data-name="Path 381"
														d="M592.562,151.893a.338.338,0,0,1-.326-.289.325.325,0,0,1,.314-.313.338.338,0,0,1,.326.288.314.314,0,0,1-.314.314Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_382"
														data-name="Path 382"
														d="M619.783,144.791a.314.314,0,0,1-.314-.3.322.322,0,0,1,.1-.218.325.325,0,0,1,.218-.1.289.289,0,0,1,.12.02.281.281,0,0,1,.1.068.3.3,0,0,1,.064.1.276.276,0,0,1,.015.121.284.284,0,0,1-.084.217.267.267,0,0,1-.1.065A.292.292,0,0,1,619.783,144.791Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_383"
														data-name="Path 383"
														d="M620.084,151.579a.281.281,0,0,1-.015.122.3.3,0,0,1-.064.1.3.3,0,0,1-.1.068.3.3,0,0,1-.121.02.3.3,0,0,1-.213-.514.3.3,0,0,1,.213-.088.274.274,0,0,1,.117.014.287.287,0,0,1,.1.061.3.3,0,0,1,.065.1A.265.265,0,0,1,620.084,151.579Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_384"
														data-name="Path 384"
														d="M620.084,153.06a.281.281,0,0,1-.015.122.311.311,0,0,1-.064.1.294.294,0,0,1-.1.068.289.289,0,0,1-.12.02.328.328,0,0,1-.314-.3.314.314,0,0,1,.314-.314.288.288,0,0,1,.217.084.314.314,0,0,1,.065.1A.291.291,0,0,1,620.084,153.06Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_385"
														data-name="Path 385"
														d="M592.562,148.43a.373.373,0,0,1,.215.1.378.378,0,0,1,.111.211.314.314,0,0,1-.314.3.327.327,0,0,1-.326-.3.337.337,0,0,1,.314-.313Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_386"
														data-name="Path 386"
														d="M620.084,147.325a.306.306,0,0,1-.019.12.3.3,0,0,1-.064.1.311.311,0,0,1-.1.068.3.3,0,0,1-.118.024.315.315,0,0,1-.314-.288.337.337,0,0,1,.314-.339.313.313,0,0,1,.3.313Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_387"
														data-name="Path 387"
														d="M620.084,150.161a.279.279,0,0,1-.015.122.3.3,0,0,1-.064.1.281.281,0,0,1-.1.068.289.289,0,0,1-.12.02.321.321,0,0,1-.138,0,.316.316,0,0,1-.123-.061.322.322,0,0,1-.085-.109.313.313,0,0,1,.085-.377.3.3,0,0,1,.123-.062.321.321,0,0,1,.138,0,.276.276,0,0,1,.117.02.267.267,0,0,1,.1.065.3.3,0,0,1,.065.1A.291.291,0,0,1,620.084,150.161Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_388"
														data-name="Path 388"
														d="M619.758,146.222a.346.346,0,0,1-.208-.116.337.337,0,0,1-.081-.223.33.33,0,0,1,.126-.213.326.326,0,0,1,.238-.063.275.275,0,0,1,.108.035.282.282,0,0,1,.086.075.286.286,0,0,1,.05.1.293.293,0,0,1,.007.114.311.311,0,0,1-.1.21A.315.315,0,0,1,619.758,146.222Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_389"
														data-name="Path 389"
														d="M620.084,148.743a.291.291,0,0,1-.02.121.277.277,0,0,1-.068.1.274.274,0,0,1-.1.064.311.311,0,0,1-.423-.273.326.326,0,0,1,.314-.326.3.3,0,0,1,.118.024.311.311,0,0,1,.1.068.3.3,0,0,1,.083.221Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_390"
														data-name="Path 390"
														d="M595.072,154.441a.384.384,0,0,1,.427-.427.376.376,0,0,1,.275.119.4.4,0,0,1,.08.131.4.4,0,0,1,.021.152h-.151Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_391"
														data-name="Path 391"
														d="M616.947,154.441h-.389a.281.281,0,0,1,0-.213.4.4,0,0,1,.137-.106.4.4,0,0,1,.34,0,.4.4,0,0,1,.137.106,1.237,1.237,0,0,1,.1.226l-.2.075A.907.907,0,0,1,616.947,154.441Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_392"
														data-name="Path 392"
														d="M617.361,143.021a.357.357,0,0,1-.314.389.405.405,0,0,1-.253-.007.4.4,0,0,1-.2-.157c-.075-.125-.075-.213.113-.225Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_393"
														data-name="Path 393"
														d="M595.85,143.083a.337.337,0,0,1-.376.377.375.375,0,0,1-.156-.018.362.362,0,0,1-.135-.079.377.377,0,0,1-.092-.127.365.365,0,0,1-.031-.153Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_394"
														data-name="Path 394"
														d="M618.352,154.5c-.113-.075-.3-.138-.3-.188a.42.42,0,0,1,.326-.251c.163,0,.326.088.326.263S618.49,154.429,618.352,154.5Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_395"
														data-name="Path 395"
														d="M593.679,154.441a.325.325,0,0,1,.3-.364.581.581,0,0,1,.339.138c.113.113.063.214-.1.226Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_396"
														data-name="Path 396"
														d="M593.691,143.083h.691a.292.292,0,0,1-.276.326C593.93,143.447,593.817,143.347,593.691,143.083Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_397"
														data-name="Path 397"
														d="M621.251,145.619c.075.1.176.175.2.276a.213.213,0,0,1-.005.1.219.219,0,0,1-.05.086.215.215,0,0,1-.084.055.222.222,0,0,1-.1.01.384.384,0,0,1-.148-.091.392.392,0,0,1-.091-.148c0-.075.113-.188.176-.276Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_398"
														data-name="Path 398"
														d="M621.151,150.45c0-.1-.188-.2-.176-.288a.405.405,0,0,1,.251-.239.2.2,0,0,1,.1.007.186.186,0,0,1,.082.056.183.183,0,0,1,.042.09.192.192,0,0,1-.009.1c0,.1-.126.188-.189.276Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_399"
														data-name="Path 399"
														d="M590.893,150.111c.1-.063.188-.188.288-.176a.3.3,0,0,1,.138.084.3.3,0,0,1,.076.142.207.207,0,0,1,0,.1.2.2,0,0,1-.043.086.216.216,0,0,1-.077.056.206.206,0,0,1-.095.013,1.241,1.241,0,0,1-.288-.188Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_400"
														data-name="Path 400"
														d="M621.176,149.1c-.1-.176-.214-.264-.214-.351a.261.261,0,0,1,.289-.226c.176.025.239.113.188.251S621.3,148.944,621.176,149.1Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_401"
														data-name="Path 401"
														d="M591.445,151.642c-.088.075-.188.188-.276.188a.36.36,0,0,1-.141-.092.347.347,0,0,1-.085-.146.2.2,0,0,1,0-.1.2.2,0,0,1,.047-.087.187.187,0,0,1,.084-.051.2.2,0,0,1,.1,0c.1,0,.188.113.276.189Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_402"
														data-name="Path 402"
														d="M621.163,144.778c0-.1-.2-.2-.188-.289s.151-.175.226-.251.226.151.239.239-.113.2-.176.288Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_403"
														data-name="Path 403"
														d="M591.408,145.819c0,.239-.088.352-.239.327a.413.413,0,0,1-.136-.09.4.4,0,0,1-.09-.136.186.186,0,0,1-.009-.1.191.191,0,0,1,.042-.09.194.194,0,0,1,.082-.056.185.185,0,0,1,.1-.006A.609.609,0,0,1,591.408,145.819Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_404"
														data-name="Path 404"
														d="M591.445,144.539c-.088,0-.189.189-.276.176a.336.336,0,0,1-.226-.226.192.192,0,0,1,0-.1.2.2,0,0,1,.043-.086.194.194,0,0,1,.077-.056.206.206,0,0,1,.1-.014c.1,0,.2.126.289.188Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_405"
														data-name="Path 405"
														d="M621.49,151.655c-.1.063-.2.176-.289.163s-.238-.15-.226-.226a.388.388,0,0,1,.091-.147.379.379,0,0,1,.148-.091c.088,0,.188.125.276.188Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_406"
														data-name="Path 406"
														d="M621.151,147.6c0-.088-.176-.176-.189-.264a.229.229,0,0,1,.011-.1.218.218,0,0,1,.05-.084.222.222,0,0,1,.081-.054.216.216,0,0,1,.1-.016c.15,0,.263.088.238.239s-.113.188-.188.276Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_407"
														data-name="Path 407"
														d="M591.445,147.375a.92.92,0,0,1-.276.176.21.21,0,0,1-.238-.213c-.013-.151,0-.264.225-.239a.835.835,0,0,1,.289.163Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_408"
														data-name="Path 408"
														d="M621.151,153.286c0-.087-.176-.175-.188-.263a.211.211,0,0,1,.01-.1.221.221,0,0,1,.054-.084.23.23,0,0,1,.087-.05.225.225,0,0,1,.1-.005.306.306,0,0,1,.226.214c0,.1-.113.188-.176.288Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_409"
														data-name="Path 409"
														d="M591.42,152.936c0,.238-.1.313-.264.313a.225.225,0,0,1-.164-.07.243.243,0,0,1-.048-.078.212.212,0,0,1-.013-.09c0-.163.087-.264.238-.239A.945.945,0,0,1,591.42,152.936Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_410"
														data-name="Path 410"
														d="M591.445,148.794a.965.965,0,0,1-.276.188.208.208,0,0,1-.238-.225c0-.151.088-.264.251-.239s.175.113.263.163Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_411"
														data-name="Path 411"
														d="M618.039,143.083h.665a.31.31,0,0,1-.015.121.282.282,0,0,1-.065.1.294.294,0,0,1-.1.069.289.289,0,0,1-.12.02.328.328,0,0,1-.136-.008.311.311,0,0,1-.12-.064.324.324,0,0,1-.081-.109A.305.305,0,0,1,618.039,143.083Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_412"
														data-name="Path 412"
														d="M592.9,154.428h-.59a.244.244,0,0,1,.013-.11.258.258,0,0,1,.059-.094.272.272,0,0,1,.094-.059.257.257,0,0,1,.11-.013C592.775,154.14,592.876,154.228,592.9,154.428Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_413"
														data-name="Path 413"
														d="M619.482,154.416a.259.259,0,0,1,.314-.263,1.108,1.108,0,0,1,.313.175v.088Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_414"
														data-name="Path 414"
														d="M592.3,143.083h.615a.277.277,0,0,1-.276.276C592.462,143.372,592.349,143.284,592.3,143.083Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_415"
														data-name="Path 415"
														d="M620.084,143.083a.275.275,0,0,1-.263.276.293.293,0,0,1-.233-.06.288.288,0,0,1-.076-.1.275.275,0,0,1-.03-.119Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_416"
														data-name="Path 416"
														d="M622.682,149.02c-.075-.138-.151-.226-.151-.289a.255.255,0,0,1,.071-.1.248.248,0,0,1,.1-.055s.176.113.163.163S622.682,148.894,622.682,149.02Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_417"
														data-name="Path 417"
														d="M590.052,144.476c-.125.076-.213.176-.264.163s-.125-.112-.188-.175.113-.176.176-.176S589.889,144.388,590.052,144.476Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_418"
														data-name="Path 418"
														d="M590.052,145.907c-.113.088-.2.176-.251.163s-.126-.113-.188-.175.125-.151.2-.163S589.889,145.819,590.052,145.907Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_419"
														data-name="Path 419"
														d="M622.908,145.9c-.113.088-.189.176-.239.176a.288.288,0,0,1-.115-.066.294.294,0,0,1-.073-.11s.1-.163.151-.163S622.782,145.82,622.908,145.9Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_420"
														data-name="Path 420"
														d="M622.92,147.338c-.138.075-.226.163-.288.151a.238.238,0,0,1-.151-.176c0-.063.113-.163.163-.163S622.795,147.25,622.92,147.338Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_421"
														data-name="Path 421"
														d="M589.763,152.747c.075.126.151.213.151.276s-.138.163-.176.151a.277.277,0,0,1-.163-.188C589.562,152.923,589.663,152.86,589.763,152.747Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_422"
														data-name="Path 422"
														d="M589.751,148.493c.087.113.175.188.163.238s-.113.138-.176.2-.151-.126-.163-.2S589.663,148.606,589.751,148.493Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_423"
														data-name="Path 423"
														d="M622.908,153.061c-.113.075-.189.163-.251.163s-.126-.1-.189-.151.113-.175.189-.188S622.795,152.923,622.908,153.061Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_424"
														data-name="Path 424"
														d="M622.682,144.727c-.088-.125-.163-.188-.163-.251s.1-.125.163-.188.163.113.176.188S622.682,144.6,622.682,144.727Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_425"
														data-name="Path 425"
														d="M590.051,147.325c-.138.088-.213.176-.288.163s-.113-.113-.163-.175.125-.151.2-.163S589.888,147.237,590.051,147.325Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_426"
														data-name="Path 426"
														d="M622.92,151.6c-.138.075-.226.163-.276.138a.407.407,0,0,1-.163-.188c-.025-.075.113-.151.163-.138A1.487,1.487,0,0,1,622.92,151.6Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_427"
														data-name="Path 427"
														d="M589.763,151.329c.076.138.164.213.151.264a.248.248,0,0,1-.068.1.242.242,0,0,1-.108.052.221.221,0,0,1-.1-.054.218.218,0,0,1-.062-.1C589.575,151.53,589.663,151.455,589.763,151.329Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_428"
														data-name="Path 428"
														d="M589.738,149.911c.088.126.176.188.176.251s-.113.163-.163.163a.242.242,0,0,1-.109-.059.259.259,0,0,1-.067-.1S589.651,150.037,589.738,149.911Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_429"
														data-name="Path 429"
														d="M622.92,150.161c-.138.088-.2.163-.264.163s-.125-.1-.188-.163.126-.15.188-.15S622.782,150.086,622.92,150.161Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_430"
														data-name="Path 430"
														d="M591.407,154.429h-.451c0-.063.113-.151.188-.176S591.307,154.241,591.407,154.429Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_431"
														data-name="Path 431"
														d="M621.427,154.417h-.44c.088,0,.151-.139.239-.164A.175.175,0,0,1,621.427,154.417Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_432"
														data-name="Path 432"
														d="M620.988,143.083h.439c-.075,0-.151.151-.226.151s-.188-.088-.276-.126Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_433"
														data-name="Path 433"
														d="M591.42,143.184a.842.842,0,0,1-.276.113c-.075,0-.138-.088-.213-.126V143.1h.426Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_434"
														data-name="Path 434"
														d="M589.55,154.441c.138-.251.226-.251.376,0Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_435"
														data-name="Path 435"
														d="M622.807,154.416h-.313v-.063s.125-.1.188-.1.113.075.176.113Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_436"
														data-name="Path 436"
														d="M624.238,150.174c-.087,0-.138.1-.175.1a.215.215,0,0,1-.1-.113.169.169,0,0,1,.043-.065.145.145,0,0,1,.07-.035A1.251,1.251,0,0,1,624.238,150.174Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_437"
														data-name="Path 437"
														d="M624.062,153.187a.533.533,0,0,1-.125-.126s.075-.063.113-.1.1.075.1.125S624.125,153.061,624.062,153.187Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_438"
														data-name="Path 438"
														d="M623.937,148.743c.088-.062.126-.125.163-.112a.214.214,0,0,1,.113.1c.013.038,0,.1-.088.1S623.937,148.794,623.937,148.743Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_439"
														data-name="Path 439"
														d="M624.075,151.8c-.062-.087-.113-.138-.113-.175a.227.227,0,0,1,.113-.1s.1,0,.1.088S624.126,151.667,624.075,151.8Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_440"
														data-name="Path 440"
														d="M588.308,150.338c0-.1-.088-.138-.088-.189s.075-.1.1-.087.1,0,.1.1S588.37,150.25,588.308,150.338Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_441"
														data-name="Path 441"
														d="M588.32,153.174c0-.063-.113-.113-.1-.138a.694.694,0,0,1,.1-.126c.038-.037.113.076.113.113S588.37,153.061,588.32,153.174Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_442"
														data-name="Path 442"
														d="M588.308,151.417c0,.087.125.125.125.163a.2.2,0,0,1-.045.068.2.2,0,0,1-.068.045s-.1,0-.1-.088S588.257,151.517,588.308,151.417Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_443"
														data-name="Path 443"
														d="M588.5,144.464c-.088,0-.138.113-.176.113a.211.211,0,0,1-.063-.052.206.206,0,0,1-.037-.074s.062-.075.1-.075S588.408,144.414,588.5,144.464Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_444"
														data-name="Path 444"
														d="M588.483,145.9c-.075.062-.113.113-.163.113s-.075,0-.1-.1.063-.088.113-.1S588.408,145.857,588.483,145.9Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_445"
														data-name="Path 445"
														d="M588.308,147.5c0-.088-.088-.138-.088-.189s.075-.1.088-.1a.119.119,0,0,1,.125.113C588.446,147.438,588.37,147.413,588.308,147.5Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_446"
														data-name="Path 446"
														d="M589.55,143.083h.351C589.776,143.259,589.738,143.259,589.55,143.083Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_447"
														data-name="Path 447"
														d="M622.469,143.083h.326C622.682,143.259,622.682,143.271,622.469,143.083Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_448"
														data-name="Path 448"
														d="M624.226,145.9c-.063.063-.113.125-.138.113a.254.254,0,0,1-.126-.1s.063-.1.1-.1S624.151,145.845,624.226,145.9Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_449"
														data-name="Path 449"
														d="M624.063,144.275c0,.063.113.1.113.126s-.063.088-.1.125-.088-.062-.1-.113S624.025,144.388,624.063,144.275Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_450"
														data-name="Path 450"
														d="M588.483,148.744c-.088,0-.138.088-.175.088s-.088,0-.088-.1a.127.127,0,0,1,.031-.061.133.133,0,0,1,.057-.039S588.4,148.681,588.483,148.744Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_451"
														data-name="Path 451"
														d="M624.075,147.476c0-.063-.1-.1-.1-.15s.062-.1.087-.088a.141.141,0,0,1,.07.035.143.143,0,0,1,.043.065S624.125,147.413,624.075,147.476Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_452"
														data-name="Path 452"
														d="M607.509,160.2a9.153,9.153,0,0,1,1.506-.113,2.51,2.51,0,0,1,1.707.5,1.638,1.638,0,0,1,.527,1.255,1.759,1.759,0,0,1-.106.674,1.741,1.741,0,0,1-.359.581,2.516,2.516,0,0,1-1.857.678,2.373,2.373,0,0,1-.628-.063v2.434h-.79Zm.79,2.936a2.268,2.268,0,0,0,.641.063c.953,0,1.531-.452,1.531-1.255s-.565-1.255-1.431-1.255a3.074,3.074,0,0,0-.741.063Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_453"
														data-name="Path 453"
														d="M614.474,166.213l-.063-.539a1.66,1.66,0,0,1-1.343.64,1.266,1.266,0,0,1-.5-.073,1.258,1.258,0,0,1-.725-.684,1.236,1.236,0,0,1-.1-.5c0-1.054.928-1.632,2.623-1.619v-.088a.9.9,0,0,0-.59-.968.914.914,0,0,0-.4-.049,2.216,2.216,0,0,0-1.142.327l-.175-.527a2.779,2.779,0,0,1,1.43-.389c1.343,0,1.669.916,1.669,1.794v1.632a6.705,6.705,0,0,0,0,1.041Zm-.126-2.221c-.866,0-1.845.138-1.845.979a.717.717,0,0,0,.038.3.7.7,0,0,0,.161.257.724.724,0,0,0,.254.165.713.713,0,0,0,.3.043,1.1,1.1,0,0,0,1.055-.741.813.813,0,0,0,0-.251Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_454"
														data-name="Path 454"
														d="M616.294,165.41a2.135,2.135,0,0,0,1.054.314c.577,0,.841-.289.841-.653s-.226-.59-.8-.8c-.791-.276-1.155-.716-1.155-1.255a1.342,1.342,0,0,1,.142-.527,1.328,1.328,0,0,1,.339-.427,1.34,1.34,0,0,1,1.025-.3,2.2,2.2,0,0,1,1.067.276l-.2.577a1.614,1.614,0,0,0-.891-.251c-.464,0-.728.264-.728.59s.264.527.828.74c.766.289,1.155.678,1.155,1.256a1.524,1.524,0,0,1-1.644,1.317,2.576,2.576,0,0,1-1.255-.3Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_455"
														data-name="Path 455"
														d="M619.87,165.41a2.139,2.139,0,0,0,1.055.314c.577,0,.84-.289.84-.653s-.225-.59-.8-.8c-.79-.276-1.154-.716-1.154-1.255a1.337,1.337,0,0,1,.481-.954,1.34,1.34,0,0,1,1.025-.3,2.2,2.2,0,0,1,1.066.276l-.2.577a1.614,1.614,0,0,0-.891-.251c-.465,0-.728.264-.728.59s.263.527.828.74c.765.289,1.155.678,1.155,1.256a1.524,1.524,0,0,1-1.644,1.317,2.571,2.571,0,0,1-1.255-.3Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_456"
														data-name="Path 456"
														d="M624.439,160.591a.465.465,0,0,1-.145.351.46.46,0,0,1-.165.1.465.465,0,0,1-.192.025.449.449,0,0,1-.185-.033.456.456,0,0,1-.157-.1.483.483,0,0,1-.1-.156.468.468,0,0,1-.032-.185.484.484,0,0,1,.143-.359.491.491,0,0,1,.359-.143.458.458,0,0,1,.19.035.464.464,0,0,1,.16.109.475.475,0,0,1,.1.166A.481.481,0,0,1,624.439,160.591Zm-.879,5.585v-4.33h.791v4.367Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_457"
														data-name="Path 457"
														d="M629.622,164a2.133,2.133,0,0,1-2.171,2.309,2.083,2.083,0,0,1-2.1-2.246,2.134,2.134,0,0,1,2.171-2.322,2.071,2.071,0,0,1,2.1,2.259Zm-3.464,0c0,.954.552,1.682,1.331,1.682s1.33-.716,1.33-1.694c0-.741-.377-1.682-1.255-1.682a1.6,1.6,0,0,0-1.406,1.732Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_458"
														data-name="Path 458"
														d="M630.639,163.1v-1.193h.7v.728a1.608,1.608,0,0,1,1.444-.828c.6,0,1.543.364,1.543,1.87v2.6h-.79v-2.51c0-.7-.264-1.255-1.017-1.255a1,1,0,0,0-1.067,1.18v2.623h-.79Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_459"
														data-name="Path 459"
														d="M637.641,166.213v-3.765h-.615v-.6h.615v-.2a2.095,2.095,0,0,1,.5-1.531,1.535,1.535,0,0,1,1.067-.4,1.809,1.809,0,0,1,.678.126l-.1.615a1.315,1.315,0,0,0-.514-.1c-.678,0-.841.59-.841,1.255v.239h1.054v.615h-1.054v3.765Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_460"
														data-name="Path 460"
														d="M644.017,164a2.133,2.133,0,0,1-.572,1.638,2.139,2.139,0,0,1-1.6.671,2.083,2.083,0,0,1-2.095-2.246,2.134,2.134,0,0,1,2.171-2.322,2.07,2.07,0,0,1,1.994,1.4A2.09,2.09,0,0,1,644.017,164Zm-3.464,0c0,.954.552,1.682,1.33,1.682s1.33-.716,1.33-1.694c0-.741-.376-1.682-1.255-1.682a1.593,1.593,0,0,0-1.393,1.732Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_461"
														data-name="Path 461"
														d="M645.033,163.214v-1.368h.69v.866a1.318,1.318,0,0,1,1.255-.966.573.573,0,0,1,.226,0v.74h-.263a1.118,1.118,0,0,0-1.067,1.017,3.359,3.359,0,0,0,0,.376V166.2h-.791Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_462"
														data-name="Path 462"
														d="M649.827,165.259a2.678,2.678,0,0,0,1.406.4c.8,0,1.255-.427,1.255-1.042s-.326-.891-1.155-1.255-1.606-.866-1.606-1.731a1.76,1.76,0,0,1,1.259-1.587,1.779,1.779,0,0,1,.711-.058,2.611,2.611,0,0,1,1.343.3l-.213.64a2.377,2.377,0,0,0-1.155-.289c-.828,0-1.154.489-1.154.9s.376.853,1.255,1.179c1.029.389,1.556.891,1.556,1.782s-.7,1.757-2.134,1.757a3.169,3.169,0,0,1-1.556-.389Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_463"
														data-name="Path 463"
														d="M654.86,164.18a1.394,1.394,0,0,0,1.494,1.519,2.92,2.92,0,0,0,1.255-.226l.125.565a3.5,3.5,0,0,1-1.443.276,2.032,2.032,0,0,1-2.234-2.209,2.121,2.121,0,0,1,2.058-2.359,1.856,1.856,0,0,1,1.807,2.058,2.358,2.358,0,0,1,0,.376Zm2.322-.564a1.115,1.115,0,0,0-1.092-1.255,1.255,1.255,0,0,0-1.255,1.255Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_464"
														data-name="Path 464"
														d="M658.939,163.1v-1.192h.7v.728a1.633,1.633,0,0,1,1.444-.828c.615,0,1.543.364,1.543,1.87v2.6h-.79v-2.51c0-.7-.264-1.255-1-1.255a1.115,1.115,0,0,0-1.067.815,1.075,1.075,0,0,0-.063.364v2.623h-.79Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_465"
														data-name="Path 465"
														d="M663.846,165.41a2.133,2.133,0,0,0,1.042.314c.577,0,.853-.289.853-.653s-.226-.589-.816-.8-1.154-.715-1.154-1.255a1.332,1.332,0,0,1,.145-.524,1.355,1.355,0,0,1,1.361-.731,2.224,2.224,0,0,1,1.079.276l-.2.578a1.668,1.668,0,0,0-.891-.251c-.464,0-.728.263-.728.59s.264.527.829.74,1.142.678,1.142,1.255a1.511,1.511,0,0,1-1.632,1.318,2.576,2.576,0,0,1-1.255-.3Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_466"
														data-name="Path 466"
														d="M671.488,164.005a2.133,2.133,0,0,1-2.183,2.309,2.083,2.083,0,0,1-2.1-2.246,2.146,2.146,0,0,1,2.171-2.322,2.076,2.076,0,0,1,.848.166,2.074,2.074,0,0,1,1.153,1.236A2.062,2.062,0,0,1,671.488,164.005Zm-3.476,0c0,.954.552,1.682,1.33,1.682s1.331-.716,1.331-1.695c0-.74-.377-1.681-1.255-1.681a1.6,1.6,0,0,0-1.406,1.732Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_467"
														data-name="Path 467"
														d="M672.493,163.214v-1.368h.69v.866a1.318,1.318,0,0,1,1.255-.967.66.66,0,0,1,.226,0v.741h-.276a1.116,1.116,0,0,0-1.067,1.016,2.966,2.966,0,0,0,0,.377V166.2h-.778Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
													<path
														id="Path_468"
														data-name="Path 468"
														d="M675.392,165.41a2.139,2.139,0,0,0,1.055.314c.577,0,.853-.289.853-.653s-.226-.59-.816-.8c-.79-.276-1.154-.716-1.154-1.255a1.337,1.337,0,0,1,.481-.954,1.34,1.34,0,0,1,1.025-.3,2.2,2.2,0,0,1,1.066.276l-.188.577a1.693,1.693,0,0,0-.9-.251c-.465,0-.728.264-.728.59s.263.527.828.74c.766.289,1.155.678,1.155,1.256a1.524,1.524,0,0,1-1.644,1.317,2.5,2.5,0,0,1-1.255-.3Z"
														transform="translate(304 108.5)"
														fill="#0060ad"
													/>
												</g>
											</svg>
										</Link>
									</div>
									<div className="pentrair single-item-brand">
										<Link
											href={'/ready-stock?brand=wika'}
											target={'_blank'}
											onMouseEnter={wikaEnter}
											onMouseLeave={wikaLeave}
											className={'Wika_Logo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="111"
												height="111"
												viewBox="0 0 111 111">
												<g
													id="Wika_Logo"
													transform="translate(-722 -543.5)">
													<path
														id="Path_495"
														data-name="Path 495"
														d="M473.5,546A55.5,55.5,0,1,0,418,490.5,55.5,55.5,0,0,0,473.5,546Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<path
														id="Path_496"
														data-name="Path 496"
														d="M442.682,500.785V479.663h56.877V495.58a.543.543,0,0,1,0,.22h-.084a.845.845,0,0,1-.1,0h-2.847a.348.348,0,0,1,0-.1V482.724a.814.814,0,0,0,0-.231H446.058a.835.835,0,0,0-.264,0,.509.509,0,0,0,0,.1V497.78a.64.64,0,0,0,0,.231h55.8v-18.1a.64.64,0,0,1,0-.231h2.932v20.908a1.106,1.106,0,0,1,0,.23H442.958A.8.8,0,0,1,442.682,500.785Z"
														transform="translate(304 108.5)"
														fill="#16489a"
													/>
													<path
														id="Path_497"
														data-name="Path 497"
														d="M447.442,484.761h3.281a.341.341,0,0,1,.061.163q.771,3.2,1.531,6.425l.067.248c.051,0,.057-.034.068-.062l.253-.816c.242-.8.49-1.592.726-2.391.146-.512.276-1.03.411-1.547l.489-1.857a.184.184,0,0,1,.045-.141H457.3a.739.739,0,0,0,.045.158l1.693,6.465a.354.354,0,0,0,.074.18.27.27,0,0,0,.056-.135c.09-.316.174-.631.264-.946q.771-2.812,1.531-5.592a.376.376,0,0,1,.05-.141h3.022a.3.3,0,0,1,0,.169l-.456,1.648-2.059,7.427c-.164.563-.321,1.159-.484,1.744q-.02.069-.045.135a.314.314,0,0,1-.192,0h-2.88c0-.056-.045-.112-.062-.163q-.675-1.906-1.283-3.82c-.124-.4-.225-.8-.338-1.21s-.219-.8-.332-1.2a.959.959,0,0,0-.045-.147c-.05,0-.056.04-.061.068l-.175.608-1.632,5.7-.056.175h-3.162l-.028-.057q-1.643-5.519-3.286-11.033C447.453,484.8,447.447,484.78,447.442,484.761Z"
														transform="translate(304 108.5)"
														fill="#16489a"
													/>
													<path
														id="Path_498"
														data-name="Path 498"
														d="M486.816,484.755h3.91a.331.331,0,0,1,.084.152l.878,2.323q1.57,4.153,3.134,8.294a.372.372,0,0,1,.034.281h-3.3a.234.234,0,0,1-.085-.146c-.242-.687-.472-1.379-.658-2.082a.653.653,0,0,0-.051-.169h-3.944a.323.323,0,0,0-.084.169l-.563,1.688c-.045.141-.084.276-.135.416a1.206,1.206,0,0,1-.05.13h-3.2v-.141l.563-1.575,3.376-9.171A1.082,1.082,0,0,0,486.816,484.755ZM489.8,491.1a.966.966,0,0,0-.029-.456c-.185-.563-.371-1.091-.562-1.637-.158-.456-.31-.906-.462-1.362,0-.045,0-.1-.067-.118a.233.233,0,0,0-.062.141,15.177,15.177,0,0,1-.518,1.429,1.421,1.421,0,0,0-.073.214c-.152.562-.3,1.125-.45,1.648v.141c0,.045.04,0,.056,0H489.8Z"
														transform="translate(304 108.5)"
														fill="#16489a"
													/>
													<path
														id="Path_499"
														data-name="Path 499"
														d="M471.682,484.766c.135,0,1.339-.033,2.633,0h.14a.467.467,0,0,1,0,.2v3.669a.136.136,0,0,0,0,.049.119.119,0,0,0,.058.076.124.124,0,0,0,.046.015.456.456,0,0,0,.147-.129l1.648-1.755,1.891-2.015.112-.112h3.59c0,.056-.034.079-.062.107l-1.125,1.125-3.072,3-.124.1a.349.349,0,0,0,.079.14c.483.659.962,1.317,1.446,1.97q1.63,2.25,3.263,4.461l.073.113c0,.045-.062.034-.09.034H479.1c-.158,0-.141,0-.248-.13l-2.689-3.876c-.113-.152-.22-.31-.326-.462l-.057-.067a.39.39,0,0,0-.09.067l-1.125,1.126a.278.278,0,0,0-.069.1.288.288,0,0,0-.021.116v2.937a.367.367,0,0,1,0,.186H471.7a.515.515,0,0,1,0-.1V484.766Z"
														transform="translate(304 108.5)"
														fill="#16489a"
													/>
													<path
														id="Path_500"
														data-name="Path 500"
														d="M465.9,484.766H468.6V495.72a.671.671,0,0,1,0,.09h-2.689V484.766Z"
														transform="translate(304 108.5)"
														fill="#16489a"
													/>
												</g>
											</svg>
										</Link>
									</div>
									<div className="ebray single-item-brand">
										<Link
											href={'/ready-stock?brand=leser'}
											target={'_blank'}
											onMouseEnter={lesarEnter}
											onMouseLeave={lesarLeave}
											className={'Leser_Logo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="116.313"
												height="116.313"
												viewBox="0 0 116.313 116.313">
												<g
													id="Leser_Logo"
													transform="translate(-537 -529.5)">
													<path
														id="Path_475"
														data-name="Path 475"
														d="M291.156,537.313A58.156,58.156,0,1,0,233,479.156,58.157,58.157,0,0,0,291.156,537.313Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<path
														id="Path_476"
														data-name="Path 476"
														d="M326.942,476.89l.078.1,2.2,2.624c.084.1.191.178.286.273s.232.232.338.351a.38.38,0,0,1,.1.262c0,.137-.041.2-.166.226a2.758,2.758,0,0,1-.31.053c-.512.042-1.028,0-1.54,0h-2.32a2.261,2.261,0,0,1-.655-.113.928.928,0,0,1-.5-.368c-.309-.47-.594-.934-.934-1.393s-.637-.927-.964-1.391c-.095-.13-.208-.255-.314-.387a.287.287,0,0,0-.233-.113h-2.165a9.09,9.09,0,0,0-1.065.042h-.142a.145.145,0,0,0-.065.005.17.17,0,0,0-.057.03.165.165,0,0,0-.04.051.159.159,0,0,0-.017.062,2.366,2.366,0,0,0-.059.452,8.35,8.35,0,0,0,.029.862c0,.5.072,1,.054,1.506a1.776,1.776,0,0,1-.047.4.6.6,0,0,1-.125.22.24.24,0,0,1-.1.075.251.251,0,0,1-.122.02h-4.9a.757.757,0,0,1-.476-.179.372.372,0,0,1-.1-.113.385.385,0,0,1-.041-.143,3.748,3.748,0,0,1-.047-.451v-7.531c0-.314,0-.63-.03-.946a6.57,6.57,0,0,1,0-.684.759.759,0,0,1,.447-.595.585.585,0,0,1,.149-.036h11.462a15.719,15.719,0,0,1,1.576.053,5.541,5.541,0,0,1,1.732.351,2.425,2.425,0,0,1,1.386,1.279,1.375,1.375,0,0,0,.125.232,1.267,1.267,0,0,1,.2.547,4.357,4.357,0,0,1,0,1.523,3.285,3.285,0,0,1-.856,1.577,5.444,5.444,0,0,1-.446.446,1.193,1.193,0,0,1-.209.142l-.743.381c-.107.059-.214.118-.309.184A1.308,1.308,0,0,0,326.942,476.89Zm-6.543-4.645h-1.607a.333.333,0,0,0-.241.1.314.314,0,0,0-.071.114.326.326,0,0,0-.02.132,24.028,24.028,0,0,0,0,2.522.228.228,0,0,0,.046.143.222.222,0,0,0,.126.083.609.609,0,0,0,.137.036h3.194a5.988,5.988,0,0,0,1.082-.118,1.825,1.825,0,0,0,.673-.262.6.6,0,0,0,.243-.285,2.088,2.088,0,0,0,.095-1.3c-.047-.172-.1-.345-.142-.517a.369.369,0,0,0-.143-.209,1.394,1.394,0,0,0-.446-.208,6.716,6.716,0,0,0-1.464-.214C321.369,472.262,320.875,472.257,320.4,472.245Z"
														transform="translate(310.119 112.276)"
														fill="#00519e"
													/>
													<path
														id="Path_477"
														data-name="Path 477"
														d="M284.945,480.777H278.42a1.783,1.783,0,0,1-.321-.036.409.409,0,0,1-.218-.108.413.413,0,0,1-.116-.213,1.557,1.557,0,0,1-.041-.274,5.116,5.116,0,0,1,0-1.19c.054-.321.095-.35.38-.392a3.187,3.187,0,0,1,.4-.036h9.148a6.322,6.322,0,0,0,.947-.054,2.111,2.111,0,0,0,.505-.143.941.941,0,0,0,.594-.814.82.82,0,0,0-.517-.845,2.611,2.611,0,0,0-.738-.2,8.29,8.29,0,0,0-1.19-.059H281.8a9.5,9.5,0,0,1-1.422-.071,5.72,5.72,0,0,1-.631-.12.583.583,0,0,1-.142-.065l-.523-.358a3.567,3.567,0,0,1-1.155-1.19,2.8,2.8,0,0,1,.358-3.36,2.967,2.967,0,0,1,1.587-.969c.155-.03.3-.066.452-.108a3.052,3.052,0,0,1,.9-.107h5.728c1.909,0,3.73.036,5.592-.047a6.021,6.021,0,0,1,1.088.047,1.873,1.873,0,0,1,.494.13.532.532,0,0,1,.249.194.517.517,0,0,1,.1.3,4.154,4.154,0,0,1,0,.981,2.369,2.369,0,0,1-.089.333.282.282,0,0,1-.08.12.292.292,0,0,1-.128.066,1.719,1.719,0,0,1-.339.059h-.9c-.4,0-.8-.03-1.19-.03h-7.268a4.629,4.629,0,0,0-1.071.066,1.58,1.58,0,0,0-.458.172.759.759,0,0,0-.4.643,1.544,1.544,0,0,0,0,.4.742.742,0,0,0,.351.5,1.67,1.67,0,0,0,.684.238,6.657,6.657,0,0,0,1.285.065l2.379-.065c.648,0,1.3-.037,1.951-.037.5,0,1,0,1.493.048a6.182,6.182,0,0,1,1.827.35,3.489,3.489,0,0,1,.827.423,1.732,1.732,0,0,1,.529.595c.113.208.226.417.333.594a2.7,2.7,0,0,1,.268.886,3.576,3.576,0,0,1-.172,1.642,2.187,2.187,0,0,1-.417.731,5.847,5.847,0,0,1-.75.738,2.289,2.289,0,0,1-1.189.505,12.393,12.393,0,0,1-1.832.059h-5.169Z"
														transform="translate(307.428 112.273)"
														fill="#00519e"
													/>
													<path
														id="Path_478"
														data-name="Path 478"
														d="M268.649,470.071h6.758a.542.542,0,0,1,.434.274,1.036,1.036,0,0,1,.149.452,2.5,2.5,0,0,1-.108,1.148.5.5,0,0,1-.083.184.2.2,0,0,1-.191.1h-6.264a10.119,10.119,0,0,1-1.04-.036,2.919,2.919,0,0,0-.756.047l-.154.047a.38.38,0,0,0-.262.356,7.541,7.541,0,0,0,.059,1.511.988.988,0,0,0,.054.233.154.154,0,0,0,.053.08.16.16,0,0,0,.089.033,2.324,2.324,0,0,0,.274,0c.881.029,1.785.029,2.647,0h.167l2.706-.037c.368,0,.731-.036,1.1-.053a2.383,2.383,0,0,1,.642.053l.113.037a.595.595,0,0,1,.434.594v.868a.172.172,0,0,1-.036.1.183.183,0,0,1-.089.064.953.953,0,0,1-.3.089h-5.389l-1.482-.036a3.24,3.24,0,0,0-.594.036.984.984,0,0,0-.25.083.243.243,0,0,0-.107.088.241.241,0,0,0-.042.131v.209a10.976,10.976,0,0,0,.066,1.528c0,.25.047.268.3.3h6c.6,0,1.19,0,1.749-.041a4.978,4.978,0,0,1,.594,0c.3.036.393.125.447.416a4.428,4.428,0,0,1,.041,1.006l-.036.345a.278.278,0,0,1-.043.149.274.274,0,0,1-.117.1,1.907,1.907,0,0,1-.363.149,3.015,3.015,0,0,1-.767.089H262.7c-.429,0-.714,0-1.077-.03a2.4,2.4,0,0,1-.5-.1.305.305,0,0,1-.183-.109.316.316,0,0,1-.073-.2,2.051,2.051,0,0,0-.036-.464c-.047-1.023,0-2.046,0-3.075v-4.967c0-.47.042-.821.072-1.231a.565.565,0,0,1,.041-.256.265.265,0,0,1,.066-.145.269.269,0,0,1,.137-.081,2.211,2.211,0,0,1,.3-.041c.214,0,.428-.047.642-.054.75,0,1.5,0,2.249.036h4.312Z"
														transform="translate(306.14 112.273)"
														fill="#00519e"
													/>
													<path
														id="Path_479"
														data-name="Path 479"
														d="M303.531,480.777h-7.322a.333.333,0,0,1-.145-.019.326.326,0,0,1-.124-.078.342.342,0,0,1-.08-.122.348.348,0,0,1-.02-.144v-7.733c0-.429-.03-.863-.042-1.291a3,3,0,0,1,.06-.833,1.177,1.177,0,0,1,.1-.28.4.4,0,0,1,.158-.18.416.416,0,0,1,.234-.058h12.658a7.451,7.451,0,0,1,1.19.06,3.647,3.647,0,0,1,.5.125.357.357,0,0,1,.184.124.361.361,0,0,1,.078.209,3.092,3.092,0,0,1-.054,1.237c-.095.375-.16.368-.458.4a8.178,8.178,0,0,1-.844.059c-1.19.042-2.409,0-3.616,0l-1.916-.036h-1.113a2.353,2.353,0,0,0-.458.072.727.727,0,0,0-.191.076.356.356,0,0,0-.117.116.328.328,0,0,0-.048.157,6.592,6.592,0,0,0,0,1.148,2.955,2.955,0,0,0,.059.452v.1a.153.153,0,0,0,.055.1.158.158,0,0,0,.106.039c.184,0,.363.036.547.042,1.231.036,2.468.029,3.7,0h1.111l1.089-.036a4.359,4.359,0,0,1,.969.036c.31.059.393.16.4.481v.684a2.475,2.475,0,0,1,0,.309.28.28,0,0,1-.213.256,1.554,1.554,0,0,1-.322.054h-7.108a.831.831,0,0,0-.262.076.287.287,0,0,0-.123.1.268.268,0,0,0-.05.15v.6c0,.345.042.689.066,1.04v.16a.168.168,0,0,0,.044.128.17.17,0,0,0,.055.041.185.185,0,0,0,.067.016c.143,0,.285.041.429.047h6.073a8.159,8.159,0,0,0,.922-.036,4.1,4.1,0,0,1,.9.036c.339.059.439.172.446.512s0,.66,0,.993a2.145,2.145,0,0,1-.042.333.3.3,0,0,1-.074.154.311.311,0,0,1-.145.09,2.434,2.434,0,0,1-.464.107,8.468,8.468,0,0,1-.874.036C307.438,480.777,305.309,480.777,303.531,480.777Z"
														transform="translate(308.833 112.274)"
														fill="#00519e"
													/>
													<path
														id="Path_480"
														data-name="Path 480"
														d="M252.76,480.774h-3.439c-.559,0-1.124,0-1.689.036a5.574,5.574,0,0,1-1.214-.036,1.527,1.527,0,0,1-.363-.12.266.266,0,0,1-.172-.274V472.66c0-.446-.03-.892-.042-1.339a2.6,2.6,0,0,1,.054-.677.908.908,0,0,1,.142-.339.5.5,0,0,1,.429-.238h3.7c.447,0,.893.047,1.339.083a1.753,1.753,0,0,1,.351.072.226.226,0,0,1,.143.069.229.229,0,0,1,.065.144,3.87,3.87,0,0,1,.036.523c0,.423-.029.845-.029,1.268v3.979a8.3,8.3,0,0,1-.036.927,3.687,3.687,0,0,0,.036.851.413.413,0,0,0,0,.089.373.373,0,0,0,.3.321,2.433,2.433,0,0,0,.625.042c.481,0,.969-.054,1.457-.054h3.8c.316,0,.63.042.94.066a.3.3,0,0,1,.3.25,1.328,1.328,0,0,1,.041.214v1.636a.278.278,0,0,1-.085.154.286.286,0,0,1-.158.078,3.809,3.809,0,0,1-.594.066H252.76Z"
														transform="translate(304.988 112.277)"
														fill="#00519e"
													/>
												</g>
											</svg>
										</Link>
									</div>
									<div className="uwt single-item-brand">
										<Link
											href={'/ready-stock?brand=bray'}
											target={'_blank'}
											onMouseEnter={brayEnter}
											onMouseLeave={brayLeave}
											className={'Bray_Logo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="112"
												height="112"
												viewBox="0 0 112 112">
												<g
													id="Bray_Logo"
													transform="translate(-349 -490.5)">
													<path
														id="Path_488"
														data-name="Path 488"
														d="M101,494a56,56,0,1,0-56-56A56,56,0,0,0,101,494Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<path
														id="Path_489"
														data-name="Path 489"
														d="M92.6,425.265c.534.076,1.068.14,1.6.254a6.779,6.779,0,0,1,3.406,1.792,4.054,4.054,0,0,1-.775,6.571c-.343.217-.712.382-1.144.623l.432.165a5.671,5.671,0,0,1,3.139,2.441,4.767,4.767,0,0,1-1.665,6.495,9.8,9.8,0,0,1-4.436,1.372c-.661,0-1.335.089-2,.089H80.155l.394-1.563c.229-.864.229-.864,1.106-.864h1.424a1.793,1.793,0,0,0,1.767-1.742V429.243a1.409,1.409,0,0,0-1.538-1.512H80.562l.636-2.466Zm-3.19,13.829v3.33a1.067,1.067,0,0,0,.623,1.054A3.456,3.456,0,0,0,94.1,442.7a4.626,4.626,0,0,0,1.03-3.52c-.178-2.987-2.631-4.322-5.211-4.03-.381,0-.534.242-.521.636C89.421,436.895,89.408,437.988,89.408,439.094Zm4.754-8.5a4.843,4.843,0,0,0-.661-2.746,2.466,2.466,0,0,0-1.868-1.271,10.561,10.561,0,0,0-1.551,0,.627.627,0,0,0-.268.041.635.635,0,0,0-.406.646V433.7c0,.266.127.419.407.406a16.753,16.753,0,0,0,1.792-.089,2.643,2.643,0,0,0,2.466-2.542C94.1,431.163,94.136,430.794,94.162,430.591Z"
														transform="translate(304 108.5)"
														fill="#ec161f"
													/>
													<path
														id="Path_490"
														data-name="Path 490"
														d="M73.165,425.265a1.265,1.265,0,0,1,0,.3v5.021H68.627c-.318,0-.432.089-.407.406s-.1.763,0,1,.572.267.877.382a6.6,6.6,0,0,1,4.118,7.46,6.48,6.48,0,0,1-6.774,5.224,6.57,6.57,0,0,1-6.164-5.656.6.6,0,0,0-.089-.381v-.953a2.1,2.1,0,0,1,.089-.382,6.587,6.587,0,0,1,4.207-5.3c.305-.127.737-.165.877-.394s0-.66,0-1-.076-.419-.394-.406H60.455v-5.326Zm-2.542,13.257a3.736,3.736,0,1,0-3.813,3.813,3.813,3.813,0,0,0,3.851-3.813Z"
														transform="translate(304 108.5)"
														fill="#ec161f"
													/>
													<path
														id="Path_491"
														data-name="Path 491"
														d="M131.339,448.092h2.91c-.2.8-.394,1.537-.6,2.275a.371.371,0,0,1-.2.152,1.293,1.293,0,0,1-.317,0h-10.5l.432-1.652c.2-.775.2-.775.992-.775h3.228a4.615,4.615,0,0,0,4.105-2.962,1.1,1.1,0,0,0,0-.673c-1.347-3.318-2.72-6.622-4.079-9.94a1.5,1.5,0,0,0-1.538-1.016h-.839v8.159a.919.919,0,0,0,.06.385.916.916,0,0,0,.545.531.921.921,0,0,0,.386.05h2.4l-.508,2.4H120.7v-1.537h-.089l-.254.279a4.93,4.93,0,0,1-6.088,1,3.585,3.585,0,0,1-.33-5.923c1.271-.864,2.707-1.576,4.067-2.338.419-.229.864-.407,1.271-.636a4.33,4.33,0,0,0,.813-.546,1.449,1.449,0,0,0,.547-1.564,1.707,1.707,0,0,0-1.347-1.271,2.694,2.694,0,0,0-2.987,1.411,10.8,10.8,0,0,0-.572,1.716c0,.114,0,.267-.115.331l-2.3,1.919v-6.66h2.389v1.512h.089a2.236,2.236,0,0,0,.216-.279,3.94,3.94,0,0,1,3.089-1.716,15.937,15.937,0,0,1,1.906,0,3.817,3.817,0,0,1,2.886,1.461l.33.369c.089-.419.165-.75.241-1.093s.1-.305.331-.305h9.2c-.127.712-.254,1.423-.394,2.135a.582.582,0,0,1-.242.127,5.811,5.811,0,0,1-.673,0c-.7,0-1.017.445-.75,1.106s.61,1.474.915,2.2l1.055,2.478c.076-.152.14-.228.178-.317l1.716-4.284a2.152,2.152,0,0,0,.089-.381c0-.572-.14-.788-.7-.8h-.814c.14-.75.267-1.449.407-2.148,0-.076.165-.165.254-.165h6.66c-.165.712-.318,1.424-.508,2.135,0,.064-.178.115-.28.127h-1.118a1.741,1.741,0,0,0-1.945,1.271q-2.059,4.971-4.131,9.952l-.114.293a5.3,5.3,0,0,1-2.339,2.936ZM120.7,439.106v-1.348c0-.444-.2-.635-.635-.61a1.814,1.814,0,0,0-.585.14,4.411,4.411,0,0,0-2.542,2.962,3.276,3.276,0,0,0,0,1.448,1.461,1.461,0,0,0,1.691,1.271,2.237,2.237,0,0,0,1.957-1.995A17.668,17.668,0,0,0,120.7,439.106Z"
														transform="translate(304 108.5)"
														fill="#ec161f"
													/>
													<path
														id="Path_492"
														data-name="Path 492"
														d="M111.8,442.639c-.178.8-.331,1.513-.5,2.212,0,.089-.165.152-.267.216s-.1,0-.152,0H99.018c.2-.763.381-1.5.6-2.237,0-.076.2-.153.305-.153h1.512a1.27,1.27,0,0,0,1.271-1.271v-6.571a1.251,1.251,0,0,0-.1-.5,1.269,1.269,0,0,0-.726-.692,1.284,1.284,0,0,0-.506-.078H99.4c.14-.762.267-1.474.407-2.186,0-.063.165-.127.241-.127h7v2.021h.077l.19-.318a3.6,3.6,0,0,1,3.813-1.805,2.542,2.542,0,0,1,1.7,3.051,2.3,2.3,0,0,1-2.669,1.7,1.8,1.8,0,0,1-1.423-1.754,2.178,2.178,0,0,0,0-.28.456.456,0,0,0-.166-.356.44.44,0,0,0-.2.011.435.435,0,0,0-.177.1,4,4,0,0,0-.7,1.093,8.434,8.434,0,0,0-.356,2.1c-.063,1.83,0,3.661,0,5.491,0,.279.115.33.356.33Z"
														transform="translate(304 108.5)"
														fill="#ec161f"
													/>
													<path
														id="Path_493"
														data-name="Path 493"
														d="M138.2,450.748a1.361,1.361,0,1,1,.961-.4A1.366,1.366,0,0,1,138.2,450.748Zm0-2.542a1.2,1.2,0,1,0,.84.346A1.192,1.192,0,0,0,138.2,448.206Z"
														transform="translate(304 108.5)"
														fill="#ec161f"
													/>
													<path
														id="Path_494"
														data-name="Path 494"
														d="M138.495,449.5a.245.245,0,0,0,.084-.055.239.239,0,0,0,.055-.085.4.4,0,0,0,0-.216.416.416,0,0,0,0-.216.371.371,0,0,0-.139-.139.529.529,0,0,0-.217,0h-.559v1.41h.2v-.6h.292l.305.6h.229Zm-.509-.61h.369a.2.2,0,0,1,.079.017.181.181,0,0,1,.065.048.184.184,0,0,1,.04.071.18.18,0,0,1,.006.08.227.227,0,0,1,0,.166.2.2,0,0,1-.139.063h-.369Z"
														transform="translate(304 108.5)"
														fill="#ec161f"
													/>
												</g>
											</svg>
										</Link>
									</div>
									<div className="baumer single-item-brand">
										<Link
											href={'/ready-stock?brand=uwt'}
											target={'_blank'}
											onMouseEnter={uwtEnter}
											onMouseLeave={uwtLeave}
											className={'UWTLlogo_Link'}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="124.74"
												height="124.74"
												viewBox="0 0 124.74 124.74">
												<g
													id="UWT_Logo"
													transform="translate(-384 -339.5)">
													<path
														id="Path_520"
														data-name="Path 520"
														d="M142.37,355.74A62.37,62.37,0,1,0,80,293.37,62.37,62.37,0,0,0,142.37,355.74Z"
														transform="translate(304 108.5)"
														fill="#fff"
													/>
													<path
														id="Path_521"
														data-name="Path 521"
														d="M168.26,295.216v6.359a.205.205,0,0,1-.039.129.2.2,0,0,1-.109.076l-.173.048a4.105,4.105,0,0,1-.532.047h-4.014a.161.161,0,0,1-.094-.034.152.152,0,0,1-.054-.084,1.669,1.669,0,0,1-.063-.26V288.974a3.647,3.647,0,0,0,0-.512,1.118,1.118,0,0,0-.125-.252.267.267,0,0,0-.079-.152.262.262,0,0,0-.156-.069H160.85a5.493,5.493,0,0,0-1.158.079.394.394,0,0,0-.213.066.41.41,0,0,0-.147.17,5.261,5.261,0,0,0-.234.52c-.086.253-.165.505-.235.788-.149.591-.344,1.159-.54,1.734q-1.314,3.751-2.613,7.487c-.165.481-.306.977-.454,1.466a7.476,7.476,0,0,1-.329.882,2.218,2.218,0,0,1-.266.457.718.718,0,0,1-.246.206.71.71,0,0,1-.309.078c-.924.047-1.839,0-2.755,0a12.806,12.806,0,0,1-1.346-.047,3.715,3.715,0,0,1-.422,0,.354.354,0,0,1-.173-.056.36.36,0,0,1-.124-.133c-.094-.2-.2-.386-.29-.591-.313-.725-.634-1.45-.923-2.183-.705-1.781-1.393-3.562-2.1-5.343-.579-1.482-1.174-2.964-1.769-4.453l-.117-.3a.661.661,0,0,0-.094-.157.164.164,0,0,0-.059-.055.169.169,0,0,0-.156,0,.172.172,0,0,0-.059.055c-.07.126-.148.244-.211.37-.157.339-.313.67-.446,1.017-.25.6-.485,1.2-.712,1.805-.36.977-.783,1.93-1.15,2.892q-.907,2.221-1.8,4.445c-.141.354-.25.725-.376,1.079s-.242.654-.375.977a4.119,4.119,0,0,1-.219.41.334.334,0,0,1-.3.2,1.935,1.935,0,0,0-.3,0c-.532.063-1.072,0-1.565.071h-1.119c-.532,0-1.064,0-1.565-.039a.457.457,0,0,1-.344-.15,2.268,2.268,0,0,1-.235-.307,4.857,4.857,0,0,1-.4-.788,15.924,15.924,0,0,1-.54-1.577c-.266-.945-.594-1.875-.923-2.8l-1.995-5.769c-.11-.339-.2-.678-.305-1.017a6.444,6.444,0,0,0-.329-.945c-.047-.1-.1-.2-.156-.292a.329.329,0,0,0-.1-.111.317.317,0,0,0-.141-.054,2.52,2.52,0,0,0-.344-.048,19.455,19.455,0,0,0-2.035-.047c-.162.008-.324.023-.485.047a.336.336,0,0,0-.2.089.343.343,0,0,0-.1.2,1.5,1.5,0,0,0,0,.268c0,.268-.047.528-.047.788v4.863a10.776,10.776,0,0,1-.078,1.576,9.54,9.54,0,0,1-.211,1.332,5.068,5.068,0,0,1-1.135,2.1,4.869,4.869,0,0,1-1.158.993,7.875,7.875,0,0,1-1.886.859,14.1,14.1,0,0,1-1.721.417l-1.4.237c-.313.047-.634.079-.947.118h-4.89c-.29,0-.571-.047-.853-.071l-.634-.047a4.043,4.043,0,0,1-.68-.087c-.423-.079-.846-.15-1.26-.244a9.029,9.029,0,0,1-2.473-.93,5.5,5.5,0,0,1-1.432-1.245,5.2,5.2,0,0,1-1.173-2.6,9.7,9.7,0,0,1-.118-1.513V284.963a3.57,3.57,0,0,1,.071-.583.375.375,0,0,1,.1-.221.369.369,0,0,1,.216-.11,2.47,2.47,0,0,1,.54-.079h.736c.368,0,.728.055,1.1.063s.72,0,1.08,0h.548a.448.448,0,0,1,.422.425c0,.166.039.331.047.489a16.2,16.2,0,0,1,0,2.136c-.039.709,0,1.324,0,1.986-.039,1.316-.039,2.624,0,3.94a8.18,8.18,0,0,0,.2,1.852,4.392,4.392,0,0,0,.227.717A2.974,2.974,0,0,0,109.38,297a4.924,4.924,0,0,0,1.306.512,8.393,8.393,0,0,0,1.237.221,16.228,16.228,0,0,0,2.465.071,6.9,6.9,0,0,0,.688-.064l.783-.094a9.963,9.963,0,0,0,2.018-.449,5.779,5.779,0,0,0,.5-.316,2.69,2.69,0,0,0,1.229-1.371,4.6,4.6,0,0,0,.32-1.2,1.5,1.5,0,0,0,0-.268V285.8a5.666,5.666,0,0,1,0-1.15,2.058,2.058,0,0,1,.094-.379.431.431,0,0,1,.173-.223.426.426,0,0,1,.273-.068h11.01a2.484,2.484,0,0,1,.641.094,1.1,1.1,0,0,1,.266.1.562.562,0,0,1,.194.162.587.587,0,0,1,.1.232l.563,2.2,1.244,4.863c.18.693.352,1.4.517,2.1a6.53,6.53,0,0,0,.242.788,1.381,1.381,0,0,0,.1.221.12.12,0,0,0,.04.031.125.125,0,0,0,.05.011.117.117,0,0,0,.049-.011.114.114,0,0,0,.041-.031.4.4,0,0,0,.172-.221c.063-.142.117-.291.18-.433l1.909-4.729c.3-.741.571-1.489.861-2.238s.6-1.576.954-2.309c.063-.134.133-.268.2-.4s.078-.126.109-.189a.451.451,0,0,1,.164-.169.444.444,0,0,1,.228-.06,4.448,4.448,0,0,1,.461-.039q.635,0,1.268-.047h4.585a3.156,3.156,0,0,1,.665.071.669.669,0,0,1,.306.151.683.683,0,0,1,.2.282q.783,1.971,1.565,3.94c.5,1.254,1.017,2.507,1.518,3.76.156.386.289.788.438,1.166s.258.615.4.922a3.431,3.431,0,0,0,.235.4c.055.079.1.189.2.181s.133-.134.157-.213c.094-.275.18-.551.25-.788.1-.394.18-.788.29-1.19.422-1.576.782-3.254,1.181-4.878.227-.9.454-1.8.736-2.687.047-.158.109-.323.164-.481a1.246,1.246,0,0,1,.1-.221.446.446,0,0,1,.407-.291l.673-.047c.86-.056,1.729,0,2.59,0l1.815.039h5.829c4.695.039,9.452,0,14.171,0h.062c.2,0,.227,0,.282.228a1.644,1.644,0,0,1,.055.276c0,.166.039.331.047.489,0,.662.062,1.316.039,1.97a2.853,2.853,0,0,1-.039.575.75.75,0,0,1,0,.134.264.264,0,0,1-.094.157.256.256,0,0,1-.172.056H178.98c-1.745.063-3.49,0-5.235,0h-3.552c-.313,0-.634-.039-.955-.047a2.741,2.741,0,0,0-.563.039.435.435,0,0,0-.1,0,.307.307,0,0,0-.258.276,4.439,4.439,0,0,0-.086.788v5.548Z"
														transform="translate(304 108.5)"
														fill="#697173"
													/>
												</g>
											</svg>
										</Link>
									</div>
								</div>
							) : (
								<img src={'/images/static/svgMobile.svg'} alt={'YGEN Automation'} />
							)}
						</div>
					</Col>
				</Row>
			</Container>
		</StyledScrollAnimation>
	);
};

export default React.memo(ScrollAnimation);
